import 'bootstrap/dist/css/bootstrap.min.css';
import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import firebase from './firebase';
import { getSession } from './auth';
import { songAudioUrl } from './songs';
import Profile from './Profile';
import Band from './Band';
import { getBandName, loadUserBands } from './bands';
import { getExperiments, subscribeExperiments } from './experiments';
import PfpCropper from './PfpCropper';
import AlbumDetails from './AlbumDetails';
import { coverPlaceholder } from './coverPlaceholder';
import { SITE_ORIGIN, siteUrl } from './siteUrl';
import { SCREENS, trackAppOpened, trackScreen } from './analytics';
import { sendPresence, subscribePresence } from './discordPresence';
import { api } from './api';
import { alacPlayableUrl } from './alac';
import { ALBUM_LINKS, albumLinkList, readAlbumLinks } from './albumLinks';
import { removeAlbumFor, saveAlbumFor } from './savedAlbums';
import { BookmarkIcon, ChevronDownIcon, ChevronUpIcon, GripIcon, MicIcon, TrashIcon, PinIcon, PrevIcon, NextIcon, PlayIcon, PauseIcon, LoopIcon, ChevronsIcon, EditIcon, CheckIcon, ShareIcon, AnalyticsIcon, PlusIcon, SpotifyIcon, YouTubeIcon, AppleMusicIcon } from './icons';
import './global.css';
import toast, { Toaster } from 'react-hot-toast';
import { Alert, Modal, Form, Button, Badge, ProgressBar as UploadProgress, Spinner } from 'react-bootstrap';
import { styled, keyframes } from '@stitches/react';

// 🎵 Styled Components
// Animated covers are uploaded untouched, so keep them a sensible size.
const MAX_GIF_COVER_BYTES = 10 * 1024 * 1024;

const AlbumCover = styled('img', {
  height: '264px',
  width: '264px',
  maxWidth: '80%',
  objectFit: 'cover',
  display: 'block',
  margin: '24px auto',
  borderRadius: '8px',
  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.25)',
});

const AlbumTitle = styled('h2', {
  textAlign: 'center',
  margin: '8px 0',
});

const AlbumArtist = styled('h4', {
  textAlign: 'center',
  color: 'gray',
});

// The album page. With `split`, a window wide enough puts the cover, names and actions in a column
// on the left and the track list on the right; narrower windows stack them, centered.
const AlbumLayout = styled('div', {
  // Buttons keep their size (a wide one like Save used to squeeze the round ones thin); a narrow column wraps instead.
  // `alignItems: center` stops the row stretching the round buttons up to a taller pill's height.
  '& .album-actions': { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  '& .album-actions > button': { flexShrink: 0, whiteSpace: 'nowrap' },
  '& .album-hint': { display: 'block', textAlign: 'center', marginTop: '-8px' },
  '& .extras-column': { display: 'none' },
  variants: {
    split: {
      true: {
        '@media (min-width: 1000px)': {
          // Fills the space above the player; the two columns scroll on their own.
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: '48px',
          width: '92%',
          maxWidth: '1240px',
          height: 'calc(100vh - var(--player-h, 120px))',
          margin: '0 auto',
          '& .album-side': {
            flex: '2 1 0', minWidth: '296px', maxWidth: '456px', display: 'flex', flexDirection: 'column',
            gap: '16px', padding: '72px 0 24px', overflowY: 'auto', // same top/bottom buffer as .album-tracks
          },
          // Both columns start at the same height, so the cover is flush with the first track.
          '& .album-header': { flex: '0 0 auto' },
          '& .album-header h2, & .album-header h4': { textAlign: 'left' },
          '& .album-header img': { width: 'min(336px, 38vh)', height: 'auto', aspectRatio: '1 / 1', maxWidth: '100%', margin: '0 0 16px' },
          '& .album-header input': { textAlign: 'left', margin: '4px 0' },
          '& .album-header .album-actions': { justifyContent: 'flex-start' },
          '& .album-header .album-hint': { textAlign: 'left', marginTop: '-8px', marginBottom: '8px' },
          '& .extras-column': { display: 'block', flex: '0 0 auto', width: '100%', margin: 0, textAlign: 'left' },
          '& .extras-below': { display: 'none' },
          '& .album-tracks': { flex: '3 1 0', maxWidth: '720px', minWidth: 0, overflowY: 'auto', padding: '72px 8px 24px 0' },
          '& .album-tracks > ul': { margin: 0, width: '100%', maxWidth: 'none' },
          '& .album-tracks > ul > li:first-child': { marginTop: 0 },
        },
      },
    },
  },
});

// The "about this album" document section under the album.
const AlbumExtras = styled('section', {
  width: '92%',
  maxWidth: '1296px',
  margin: '48px auto 24px',
  textAlign: 'left',
  '& h3': { fontSize: '1.25rem', marginBottom: '16px' },
});

const SongList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '32px auto',
  maxWidth: '1000px',
  width: '90%',
});

const Song = styled('li', {
  position: 'relative',
  padding: '16px 24px',
  margin: '8px 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '6px',
  cursor: 'pointer',
  backgroundColor: 'var(--song-bg)',
  '&:hover': {
    backgroundColor: 'var(--song-bg-hover)',
  },
  variants: {
    // The song the player is on (playing or paused), wherever it's listed.
    current: {
      true: {
        backgroundColor: 'var(--song-current-bg)',
        boxShadow: 'inset 4px 0 0 #1db954',
        '&:hover': { backgroundColor: 'var(--song-current-bg-hover)' },
      },
    },
  },
});

const equalize = keyframes({
  '0%, 100%': { transform: 'scaleY(0.25)' },
  '50%': { transform: 'scaleY(1)' },
});

// Three bouncing bars that stand still (at different heights) when the song is paused.
const Equalizer = styled('span', {
  display: 'inline-flex',
  alignItems: 'flex-end',
  gap: '2px',
  width: '16px',
  height: '16px',
  flexShrink: 0,
  color: '#1db954',
  '& > i': {
    display: 'block',
    flex: 1,
    height: '100%',
    backgroundColor: 'currentColor',
    borderRadius: '1px',
    transformOrigin: 'bottom',
    animation: `${equalize} 0.9s ease-in-out infinite`,
  },
  '& > i:nth-child(2)': { animationDuration: '0.7s', animationDelay: '-0.3s' },
  '& > i:nth-child(3)': { animationDuration: '1.1s', animationDelay: '-0.6s' },
  '@media (prefers-reduced-motion: reduce)': {
    '& > i': { animation: 'none', transform: 'scaleY(0.6)' },
  },
  variants: {
    paused: {
      true: {
        '& > i': { animation: 'none' },
        '& > i:nth-child(1)': { transform: 'scaleY(0.45)' },
        '& > i:nth-child(2)': { transform: 'scaleY(0.9)' },
        '& > i:nth-child(3)': { transform: 'scaleY(0.6)' },
      },
    },
  },
});

const NowPlayingIcon = ({ playing, style }) => (
  <Equalizer paused={playing ? undefined : "true"} style={style} role="img"
    aria-label={playing ? "Playing" : "Paused"} title={playing ? "Playing" : "Paused"}>
    <i /><i /><i />
  </Equalizer>
);

// Wraps a sidebar thumbnail so a small equalizer can sit on its corner.
const ThumbWrap = styled('span', {
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
});

const ThumbBadge = styled('span', {
  position: 'absolute',
  right: '-4px',
  bottom: '-4px',
  display: 'inline-flex',
  padding: '4px',
  borderRadius: '4px',
  backgroundColor: '#111',
  boxShadow: '0 0 0 1px #1db954',
});

const PlayerBar = styled('div', {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  height: '120px',
  backgroundColor: '#222',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '4px 24px',
  transition: 'height 0.3s',
  overflow: 'hidden',
});

// "Album · Artist" under the song title in the player; each part links somewhere.
const PlayerSubtitle = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'baseline',
  gap: '8px',
  maxWidth: '100%',
  fontSize: '12px',
  color: '#aaa',
  whiteSpace: 'nowrap',
});

const PlayerLink = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#bbb',
  fontSize: 'inherit',
  cursor: 'pointer',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': { color: '#1db954', textDecoration: 'underline' },
});

const PlayerHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '90%',
  cursor: 'pointer',
  fontWeight: 'bold',
});

const PlayButton = styled('button', {
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
  '&:hover': {
    color: '#1db954',
  },
});

const LoopButton = styled(PlayButton, {
  fontSize: '20px',
  color: '#aaa',
  variants: {
    active: {
      true: { color: '#1db954' },
    },
  },
});

const ProgressWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  gap: '8px',
  minWidth: 0,
});

// Range slider with a green filled track. Set the fill with style={{ '--fill': '40%' }}.
const thumb = {
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  border: 'none',
  background: '#fff',
  boxShadow: '0 0 2px rgba(0,0,0,0.6)',
};
const track = {
  height: '4px',
  borderRadius: '2px',
  background: 'linear-gradient(to right, #1db954 var(--fill, 0%), #555 var(--fill, 0%))',
};
const Slider = styled('input', {
  appearance: 'none',
  WebkitAppearance: 'none',
  background: 'transparent',
  height: '16px',
  margin: 0,
  cursor: 'pointer',
  '&:focus': { outline: 'none' },
  '&::-webkit-slider-runnable-track': track,
  '&::-moz-range-track': track,
  '&::-webkit-slider-thumb': { ...thumb, appearance: 'none', WebkitAppearance: 'none', marginTop: '-4px' },
  '&::-moz-range-thumb': thumb,
  '&:focus-visible::-webkit-slider-thumb': { boxShadow: '0 0 0 3px rgba(29,185,84,0.6)' },
  '&:focus-visible::-moz-range-thumb': { boxShadow: '0 0 0 3px rgba(29,185,84,0.6)' },
});

const ProgressBar = styled(Slider, {
  flexGrow: 1,
  minWidth: 0,
});

const VolumeSlider = styled(Slider, {
  width: '96px',
});

const PlayerControlsContainer = styled('div', {
  display: 'flex',
  width: '90%',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
});

const ControlsWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '@media (max-width: 550px)': {
    justifyContent: 'center',
    width: '100%',
  },
});

const OptionsMenu = styled('div', {
  position: 'absolute',
  bottom: '100%',
  right: '0',
  background: 'var(--menu-bg)',
  border: '1px solid var(--menu-border)',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: '190px',
  padding: '6px',
  boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
  zIndex: 100,
  '& button': {
    background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
    textAlign: 'left', whiteSpace: 'nowrap', fontSize: '14px', lineHeight: 1.3,
    padding: '9px 12px', borderRadius: '6px',
    '&:hover, &:focus-visible': { background: 'var(--song-bg-hover)', outline: 'none' },
  },
  variants: {
    // Rows in the top half of the window open the menu downward so it isn't cut off at the top.
    below: {
      true: { bottom: 'auto', top: '100%' },
    },
  },
});

// 🎵 Sidebar (collapsible)
const SidebarContainer = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  backgroundColor: '#111',
  color: '#fff',
  overflow: 'hidden',
  transition: 'width 0.3s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

// The playlists and albums: the only part of the sidebar that scrolls. The brand and search above it
// and the profile button below it stay put.
const SidebarScroll = styled('div', {
  flex: '1 1 0',
  minHeight: 0,
  width: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  scrollbarWidth: 'thin',
  scrollbarColor: '#444 transparent',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-thumb': { background: '#444', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#666' },
});

const SidebarBrand = styled('div', {
  flex: '0 0 auto',
  width: '100%',
  padding: '8px 16px',
  fontWeight: 'bold',
  fontSize: '20px',
  textAlign: 'center',
  borderBottom: '1px solid #333',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

const SidebarHeader = styled('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px',
  fontWeight: 'bold',
});

const SidebarToggle = styled('button', {
  background: 'none',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '18px',
  '&:hover': { color: '#1db954' },
});

const AlbumThumb = styled('img', {
  width: '40px',
  height: '40px',
  borderRadius: '4px',
  objectFit: 'cover',
});

const AlbumTextWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '8px',
  overflow: 'hidden',
});

const AlbumName = styled('span', {
  fontWeight: 'bold',
});

const marquee = keyframes({
  '0%': { transform: 'translateX(100%)' },
  '100%': { transform: 'translateX(-100%)' },
});

const SidebarArtist = styled('span', {
  fontSize: '12px',
  color: 'gray',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '128px',
  variants: {
    scroll: {
      true: {
        animation: `${marquee} 12s linear infinite`,
        display: 'inline-block',
      },
    },
  },
});

// The signed-in user's avatar and name, fixed at the bottom of the sidebar.
const ProfileButton = styled('button', {
  flex: '0 0 auto',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  background: '#111',
  border: 'none',
  borderTop: '1px solid #333',
  color: '#fff',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { color: '#1db954' },
  '& .profile-avatar': {
    flex: '0 0 auto', width: '32px', height: '32px', borderRadius: '50%', background: '#555 center / cover no-repeat',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color: '#fff',
  },
  '& .profile-name': { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  variants: {
    collapsed: { true: { justifyContent: 'center', padding: '8px 0' } },
  },
});

const SearchInput = styled('input', {
  flex: '0 0 auto',
  width: 'calc(100% - 16px)',
  margin: '8px',
  padding: '8px',
  borderRadius: '6px',
  border: '1px solid #333',
  backgroundColor: '#222',
  color: '#fff',
  fontSize: '14px',
  '&::placeholder': { color: '#888' },
  '&:focus': { outline: 'none', borderColor: '#1db954' },
});

const EditButton = styled('button', {
  display: 'block',
  margin: '0 auto',
  padding: '4px 16px',
  borderRadius: '16px',
  border: '1px solid #888',
  background: 'none',
  cursor: 'pointer',
  '&:hover': { borderColor: '#1db954', color: '#1db954' },
});

// Round icon-only version of EditButton (edit / share on the album page).
const ActionButton = styled(EditButton, {
  margin: 0,
  padding: '8px',
  borderRadius: '50%',
  fontSize: '18px',
  lineHeight: 1,
  color: 'inherit',
  aspectRatio: '1 / 1', // always a circle, never an oval
});

const EditInput = styled('input', {
  flexGrow: 1,
  minWidth: 0,
  padding: '4px 8px',
  border: '1px solid var(--edit-border)',
  borderRadius: '6px',
  background: 'var(--edit-bg)',
  color: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:focus': { borderColor: 'var(--edit-accent)', boxShadow: '0 0 0 3px var(--edit-ring)' },
  '&::placeholder': { color: 'inherit', opacity: 0.45 },
  variants: {
    centered: { true: { textAlign: 'center', display: 'block', margin: '4px auto' } },
  },
});

const IconButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  marginLeft: '8px',
});

// The small icon buttons on a song row in edit mode (move, artists, delete).
const RowAction = styled('button', {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '32px', height: '32px', padding: 0, fontSize: '18px', lineHeight: 1,
  border: 'none', borderRadius: '8px', background: 'none', color: 'inherit', opacity: 0.65, cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s, opacity 0.15s',
  '&:hover:not(:disabled)': { opacity: 1, background: 'var(--edit-bg-hover)', color: 'var(--edit-accent)' },
  '&:focus-visible': { outline: 'none', opacity: 1, boxShadow: '0 0 0 3px var(--edit-ring)' },
  '&:disabled': { opacity: 0.25, cursor: 'default' },
  variants: {
    danger: { true: { '&:hover:not(:disabled)': { opacity: 1, background: 'rgba(224, 36, 94, 0.14)', color: '#e0245e' } } },
  },
});

const AlbumItemRow = styled('div', {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: '8px',
  borderRadius: '6px',
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#333' },
  variants: {
    // Subtle: just a step lighter than the hover gray.
    active: { true: { backgroundColor: '#3d3d3d', '&:hover': { backgroundColor: '#3d3d3d' } } },
  },
});

// Playlists shown above the albums. "saved" is the iOS saved-songs list; more can be added here.
// Existing accounts have no email, so they get a reminder; dismissing it snoozes it for a week.
const EMAIL_PROMPT_KEY = 'stew_email_prompt_snoozed_until';
const emailPromptSnoozed = () => {
  try { return Number(window.localStorage.getItem(EMAIL_PROMPT_KEY)) > Date.now(); } catch (e) { return false; }
};
const snoozeEmailPrompt = () => {
  try { window.localStorage.setItem(EMAIL_PROMPT_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch (e) { /* storage unavailable */ }
};

// Album status tags are built but hidden for now; flip this to show and edit them.
const SHOW_ALBUM_STATUS = false;
const DEFAULT_STATUS = 'In Progress';
const STATUS_SUGGESTIONS = ['In Progress', 'Released'];
// Same colours as iOS: green for Released, yellow for everything else.
const statusVariant = (status) => (status === 'Released' ? 'success' : 'warning');

// Activity that decides list order is stored on the user's record so every browser (and iOS) sees it:
//   users/<id>/albumOpened/<albumId>  = ms timestamp of the last time the album was opened
//   users/<id>/pinnedAlbums/<albumId> = ms timestamp of when it was pinned

// Pinned albums first (in the order they were pinned), then the rest by most recently opened.
// Albums never opened keep their original order after those.
const sortAlbumIds = (albums, opened, pinned) => {
  const ids = albums.map(a => a.id);
  const pinnedIds = ids.filter(id => pinned[id]).sort((a, b) => pinned[a] - pinned[b]);
  const rest = ids.map((id, i) => ({ id, i })).filter(({ id }) => !pinned[id])
    .sort((a, b) => (opened[b.id] || 0) - (opened[a.id] || 0) || a.i - b.i)
    .map(({ id }) => id);
  return [...pinnedIds, ...rest];
};

// Applies a pin change, an album being opened, or a new album to an existing order without
// re-sorting anything else: pinned albums gather at the top, and an unpinned `movedId` goes to the
// top of the rest.
const arrangeAlbumIds = (order, pinned, movedId) => {
  const pinnedIds = order.filter(id => pinned[id]).sort((a, b) => pinned[a] - pinned[b]);
  const rest = order.filter(id => !pinned[id] && id !== movedId);
  if (movedId && !pinned[movedId]) rest.unshift(movedId);
  return [...pinnedIds, ...rest];
};

// One-time move of the older per-browser order data (localStorage) into the user's record.
const migrateLocalActivity = async (userRef, userId) => {
  const legacy = { albumOpened: `stew_album_last_opened_${userId}` };
  for (const [field, key] of Object.entries(legacy)) {
    let local = null;
    try { local = JSON.parse(window.localStorage.getItem(key)); } catch (e) { /* ignore */ }
    if (!local) continue;
    const remote = (await userRef.child(field).once('value')).val() || {};
    const updates = {};
    Object.entries(local).forEach(([id, time]) => { if (!(id in remote)) updates[id] = time; });
    if (Object.keys(updates).length) await userRef.child(field).update(updates);
    try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
  }
};

const PLAYLISTS = [
  { id: 'saved', name: 'Saved songs', icon: '♥' },
];

// Version list for the Versions dialog, newest first (like the iOS Song.getSortedVersions()).
const toVersionList = (versions) => Object.entries(versions || {}).map(([id, v]) => ({
  id,
  versionNumber: v.versionNumber || 1,
  url: v.url || "",
  timestamp: v.timestamp || 0,
  description: v.description || "",
  visible: !!v.visible,
})).sort((a, b) => b.versionNumber - a.versionNumber);

// Which versions of a song get their own row in the album view: the default one always, plus any
// other version ticked "Show in album" (`versions/<id>/visible`, which iOS simply ignores).
const albumVersionInfo = (versions, defaultVersion) => {
  const list = Array.isArray(versions) ? versions : toVersionList(versions);
  const def = list.find(v => v.id === defaultVersion);
  return {
    defaultNumber: def ? def.versionNumber : 1,
    extras: list
      .filter(v => v.visible && v.id !== defaultVersion && v.url)
      .map(v => ({ id: v.id, number: v.versionNumber, url: v.url }))
      .sort((a, b) => a.number - b.number),
  };
};

// The rows of the shown list. With `expand`, a song with several visible versions gets one row per
// version, in version order, each named "Song (v2)"; otherwise there's one row per song.
// `versionId` is null for the row that follows the song's default version.
const buildRows = ({ songs, songNames, songURLs, songVersions, songMeta, songArtistKeys, selectedAlbumId, albumUrl, albumName, artistName }, expand) => {
  const rows = [];
  songs.forEach((id, i) => {
    const info = expand && songVersions[i];
    const extras = info ? info.extras : [];
    const meta = songMeta[i];
    const entry = (versionId, number, url) => ({
      songIndex: i,
      id,
      versionId,
      number,
      base: songNames[i],
      label: extras.length ? ` (v${number})` : '',
      name: songNames[i] + (extras.length ? ` (v${number})` : ''),
      url,
      albumId: (meta && meta.albumId) || selectedAlbumId,
      albumTitle: (meta && meta.albumTitle) || albumName || '',
      artist: (meta && meta.artist) || artistName || '',
      artistKeys: (songArtistKeys && songArtistKeys[id]) || [],
      coverUrl: (meta && meta.coverUrl) || albumUrl || '',
    });
    const entries = [entry(null, info ? info.defaultNumber : 1, songURLs[i])];
    extras.forEach(x => entries.push(entry(x.id, x.number, x.url)));
    entries.sort((a, b) => a.number - b.number);
    rows.push(...entries);
  });
  return rows;
};

// Album list view preferences (this browser only).
const ALBUM_PREFS_KEY = 'stew_album_list_prefs';
const loadAlbumPrefs = () => {
  try {
    const prefs = JSON.parse(window.localStorage.getItem(ALBUM_PREFS_KEY)) || {};
    return { albumSort: prefs.albumSort === 'alpha' ? 'alpha' : 'recent', showPinned: prefs.showPinned !== false };
  } catch (e) {
    return { albumSort: 'recent', showPinned: true };
  }
};

// Case- and accent-insensitive text for search matching.
const normalizeText = (text) => (text || "").normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Drops a deleted version's file from the service worker's song cache (see public/stew-sw.js).
const evictCachedSong = (url) => {
  if (window.caches) window.caches.open('stew-audio-v1').then(cache => cache.delete(url)).catch(() => {});
};

class App extends Component {
  constructor(props) {
    super(props);
    this.db = firebase.database().ref();
    this.storage = firebase.storage().ref();

    // Display names by username, for the artists of albums (see resolveArtists).
    this.userNameCache = new Map();

    this.state = {
      userId: '',
      loggedOut: false,
      // The artists of each album seen so far: albumId -> [{ username, name }], the owner first, then
      // the collaborators. Same data as iOS; collaborators are credited as artists.
      albumArtists: {},
      // Which of the album's artists each song credits: songId -> [artist key]. Songs without an entry
      // show the album's artists.
      songArtistKeys: {},
      artistsDialog: null,
      songArtistsDialog: null,
      sidebarUser: { name: '', pfpURL: '' },
      editMode: false,
      analyticsMode: false,
      songListens: {},
      uploadingCover: false,
      coverBust: '',
      prompt: null,
      versionsDialog: null,
      moveDialog: null,
      mergeDialog: null,
      filterOpen: false,
      ...loadAlbumPrefs(),
      emailStatus: null,
      emailPromptHidden: emailPromptSnoozed(),
      searchQuery: '',
      songIndex: [],
      songIndexLoading: false,
      songIndexes: [],
      songMeta: [],
      songVersions: [],
      savedIds: {},
      userAlbums: [],
      selectedPlaylistId: '',
      playlistLoading: false,
      selectedAlbumId: '',
      albumName: '',
      artistName: '',
      albumStatus: DEFAULT_STATUS,
      albumUrl: '',
      albumOwnerId: '',
      albumOwners: [],
      albumCollaborators: [],
      albumDescription: '',
      albumLinks: {},
      shareDialog: null,
      experiments: getExperiments(),
      songs: [],
      songNames: [],
      songURLs: [],
      // What the player is working through. It's a snapshot of the list a song was started from,
      // so playback carries on (with the right title and prev/next) while browsing other albums.
      queue: [],
      queueIndex: null,
      queueSource: '',
      isPlaying: false,
      isBuffering: false,
      lastOpened: {},
      pinned: {},
      albumOrder: [],
      cropFile: null,
      dragOverIndex: null,
      menuOpenIndex: null,
      progress: 0,
      duration: 0,
      isLooping: false,
      volume: 1.0,
      playerCollapsed: false,
      sidebarCollapsed: false,
    };

    this.audioRef = React.createRef();
    this.coverInputRef = React.createRef();
    this.pendingMedia = {}; // albumId -> storage paths uploaded but not yet saved into the description
    this.listenPending = null; // id of the song started but not yet counted as a listen
    this.searchInputRef = React.createRef();
    // Every song in the user's albums, for search. Built on demand and refreshed when stale.
    this.songIndexPromise = null;
    this.songIndexAt = 0;
  }

  // The name and picture on the sidebar's profile button.
  loadSidebarUser = (username) => {
    firebase.database().ref('users').child(username).once('value').then(snap => {
      const v = snap.val() || {};
      this.setState({ sidebarUser: { name: v.name || '', pfpURL: v.pfpURL || '' } });
    }).catch(() => {});
  };

  componentDidUpdate(prevProps, prevState) {
    const playingAlbumId = (q) => { const item = q && q.queue[q.queueIndex]; return item ? item.albumId : ''; };
    const nowAlbumId = playingAlbumId(this.state);
    if (nowAlbumId && nowAlbumId !== playingAlbumId(prevState) && !this.state.albumArtists[nowAlbumId]) {
      this.db.child('albums').child(nowAlbumId).once('value')
        .then(snap => snap.exists() && this.resolveArtists(nowAlbumId, snap.val())).catch(() => {});
    }
    if (this.state.menuOpenIndex !== null) this.watchMenuDismiss();
    else if (this.stopMenuDismiss) this.stopMenuDismiss();
    // Picks up a new name or picture after visiting the profile page.
    const was = prevProps.location ? prevProps.location.pathname : '';
    const now = this.props.location ? this.props.location.pathname : '';
    if (was !== now && (was === '/profile') && this.state.userId) this.loadSidebarUser(this.state.userId);
    if (was !== now && was === '/profile') trackScreen(this.state.selectedAlbumId ? SCREENS.album : SCREENS.home);
    // Moving to an album link from inside the app (a link click, or the back button).
    const linkAlbumId = was !== now ? this.albumIdFromPath() : '';
    if (linkAlbumId && linkAlbumId !== this.state.selectedAlbumId && this.state.userId) this.openFromLink(linkAlbumId);
  }

  componentDidMount() {
    // Only ever load the logged-in user's albums, regardless of the URL.
    const session = getSession();
    if (!session) {
      this.setState({ loggedOut: true });
      return;
    }
    const userId = session.username;
    this.setState({ userId });
    this.loadSidebarUser(userId);
    api('email-status', { username: userId })
      .then((emailStatus) => this.setState({ emailStatus }))
      .catch(() => {});
    // Which songs show a filled heart in the album view.
    this.db.child('playlists').child(userId).once('value')
      .then((snap) => {
        const savedIds = {};
        snap.forEach((child) => { savedIds[child.key] = true; });
        this.setState((prev) => ({ savedIds: { ...savedIds, ...prev.savedIds } }));
      })
      .catch(() => {});

    trackAppOpened();
    trackScreen(SCREENS.home);
    this.loadAlbumList(userId).catch(() => toast.error("Couldn't load albums ❌"));
    this.unsubscribeExperiments = subscribeExperiments((experiments) => this.setState({ experiments }));
    // When the Discord helper is found (or the switch is turned on mid-song), share what's already playing.
    this.unsubscribePresence = subscribePresence((status) => { if (status === 'connected') this.pushPresence(); });

    if (this.audioRef.current) {
      this.audioRef.current.volume = this.state.volume;
    }
  }

  getUserName = (username) => {
    if (!this.userNameCache.has(username)) {
      const promise = this.db.child('users').child(username).child('name').once('value')
        .then(snap => snap.val() || '')
        .catch(() => { this.userNameCache.delete(username); return ''; });
      this.userNameCache.set(username, promise);
    }
    return this.userNameCache.get(username);
  };

  // Works out an album's artists from its data. Every artist has a `key` used to say who is credited:
  // the owner (shown as the album's `artist` text, key = their username), each collaborator (key =
  // username, shown by profile name), each band (`bands/<id>`, key = band id, links to the band page)
  // and each artist who isn't on Stew (`externalArtists/<key>`, just a
  // name, so no profile link). `hidden` artists (`hiddenArtists/<key>`) stay off the album's artist line.
  resolveArtists = async (albumId, album) => {
    if (!album) return;
    const primary = album.userId || '';
    const hidden = album.hiddenArtists || {};
    const owners = this.ownersOf(album);
    const others = [...owners, ...Object.keys(album.collaborators || {})]
      .filter((u, i, all) => u !== primary && all.indexOf(u) === i);
    // The lead artist is the primary owner under their account name; the album's `artist` text (kept
    // for the iOS app) is not used, except for an album with no owner.
    const leadName = primary ? (await this.getUserName(primary)) || primary : album.artist || '';
    const people = await Promise.all(others.map(async (username) => ({
      key: username, username, name: (await this.getUserName(username)) || username,
      role: owners.includes(username) ? 'owner' : 'collaborator',
    })));
    // Owners first, then collaborators, then artists who aren't on Stew.
    people.sort((a, b) => (a.role === b.role ? 0 : a.role === 'owner' ? -1 : 1));
    const external = Object.entries(album.externalArtists || {})
      .filter(([, v]) => v && v.name)
      .map(([key, v]) => ({ key, username: '', name: v.name, external: true }));
    // Bands credited on the album (`albums/<id>/bands/<bandId>`). One that was deleted is left out.
    const bands = (await Promise.all(Object.keys(album.bands || {}).map(async (bandId) => (
      { key: bandId, username: '', bandId, band: true, name: await getBandName(bandId) }))))
      .filter(b => b.name);
    const artists = [
      ...(leadName ? [{ key: primary || 'owner', username: primary, name: leadName, role: 'owner' }] : []),
      ...people,
      ...bands,
      ...external,
    ].map(a => ({ ...a, hidden: !!hidden[a.key] }));
    this.setState(prev => ({ albumArtists: { ...prev.albumArtists, [albumId]: artists } }));
  };

  refreshArtists = (albumId) => this.db.child('albums').child(albumId).once('value')
    .then(snap => snap.exists() && this.resolveArtists(albumId, snap.val())).catch(() => {});

  // The artists to show for an album, or for one of its songs when `songKeys` (that song's chosen
  // artists) is given. Falls back to the album's artist text until the artists are resolved.
  artistsOf = (albumId, fallbackName, songKeys) => {
    const all = this.state.albumArtists[albumId];
    if (!all) return fallbackName ? [{ key: '', username: '', name: fallbackName }] : [];
    if (songKeys && songKeys.length) {
      const picked = all.filter(a => songKeys.includes(a.key));
      if (picked.length) return picked;
    }
    return all.filter(a => !a.hidden);
  };

  artistLine = (albumId, fallbackName, songKeys) =>
    this.artistsOf(albumId, fallbackName, songKeys).map(a => a.name).join(', ');

  // What the sidebar needs to list an album, from its record.
  albumSummary = (id, album) => ({
    id,
    name: album?.name || "",
    coverUrl: album?.albumCoverURL || null,
    artist: album?.artist || "",
    status: album?.status || DEFAULT_STATUS,
  });

  // The library is the albums the person is credited on (`users/<id>/albums`, which also still holds older
  // "listener" entries) plus the ones they saved from a link (`users/<id>/savedAlbums`).
  loadAlbumList = async (userId) => {
    const userRef = this.db.child('users').child(userId);
    await migrateLocalActivity(userRef, userId).catch(() => {});
    const [albumsSnap, savedSnap, openedSnap, pinnedSnap] = await Promise.all(
      ['albums', 'savedAlbums', 'albumOpened', 'pinnedAlbums'].map(field => userRef.child(field).once('value')));
    const ids = [...new Set([...Object.keys(albumsSnap.val() || {}), ...Object.keys(savedSnap.val() || {})])];
    const linkAlbumId = this.albumIdFromPath();
    if (!ids.length && !linkAlbumId) return;
    const albumData = await Promise.all(ids.map(id =>
      this.db.child('albums').child(id).once('value').then(s => {
        if (s.exists()) this.resolveArtists(id, s.val());
        return this.albumSummary(id, s.val());
      })
    ));
    const userAlbums = albumData.filter(a => a.name !== "");
    const lastOpened = openedSnap.val() || {};
    const pinned = pinnedSnap.val() || {};
    const albumOrder = sortAlbumIds(userAlbums, lastOpened, pinned);
    this.setState({ userAlbums, lastOpened, pinned, albumOrder });
    this.watchPinned(userRef.child('pinnedAlbums'));
    // Arrived from an album link: show that album (it isn't added to the library unless it already is).
    if (linkAlbumId) { this.openFromLink(linkAlbumId); return; }
    // Open the album opened most recently (pinned or not), else the first in the list.
    const recent = userAlbums.reduce((best, a) => (lastOpened[a.id] || 0) > (lastOpened[best.id] || 0) ? a : best, userAlbums[0]);
    const first = recent && lastOpened[recent.id] ? recent.id : albumOrder[0];
    if (first) this.loadAlbum(first);
  };

  // The album in an `/a/<albumId>` link, if that's the page being shown.
  albumIdFromPath = () => {
    const pathname = this.props.location ? this.props.location.pathname : '';
    const match = /^\/a\/([^/]+)\/?$/.exec(pathname);
    if (!match) return '';
    try {
      const id = decodeURIComponent(match[1]);
      return /[.#$[\]/]/.test(id) ? '' : id; // not a valid Firebase key, so it can't be an album
    } catch (e) { return ''; }
  };

  // Shows an album from a link. Looking at it saves nothing; only an album already in the library counts
  // as opened. A link made by the Save button on the share page (`?save=1`) saves it once, then drops the flag.
  openFromLink = (albumId) => {
    if (this.state.userAlbums.some(a => a.id === albumId)) this.markAlbumOpened(albumId);
    this.loadAlbum(albumId);
    const params = new URLSearchParams(this.props.location ? this.props.location.search : '');
    if (params.get('save') === '1') {
      this.props.history.replace(`/a/${encodeURIComponent(albumId)}`);
      this.saveAlbum(albumId);
    }
  };

  isInLibrary = (albumId) => this.state.userAlbums.some(a => a.id === albumId);

  // Attaches the album to the person's account. Safe to press twice.
  saveAlbum = async (albumId) => {
    const userId = this.state.userId;
    if (!userId || !albumId || this.isInLibrary(albumId) || this.savingAlbum) return;
    this.savingAlbum = true;
    try {
      const snap = await this.db.child('albums').child(albumId).once('value');
      if (!snap.exists() || !snap.val().name) { toast.error("This album isn't available ❌"); return; }
      await saveAlbumFor(userId, albumId);
      if (!this.isInLibrary(albumId)) {
        this.setState(prev => ({ userAlbums: [...prev.userAlbums, this.albumSummary(albumId, snap.val())] }));
      }
      this.markAlbumOpened(albumId);
      toast.success("Saved to your albums ✅");
    } catch (e) {
      toast.error("Couldn't save album ❌");
    } finally {
      this.savingAlbum = false;
    }
  };

  // Takes the album out of the person's library: forgets the save (and an older listener entry) and its
  // pin. The album, its songs and its credits are untouched, and anyone with the link can still open it.
  // Albums the person is credited on stay (they leave through People in Share).
  removeAlbum = async (albumId) => {
    const userId = this.state.userId;
    if (!userId || !this.isInLibrary(albumId)) return;
    const name = (this.state.userAlbums.find(a => a.id === albumId) || {}).name;
    if (!window.confirm(`Remove "${name}" from your albums? The album itself isn't deleted, and you can save it again from its link.`)) return;
    try {
      await removeAlbumFor(userId, albumId);
    } catch (e) {
      toast.error("Couldn't remove album ❌");
      return;
    }
    this.setState(prev => {
      const lastOpened = { ...prev.lastOpened }; delete lastOpened[albumId];
      const pinned = { ...prev.pinned }; delete pinned[albumId];
      return {
        userAlbums: prev.userAlbums.filter(a => a.id !== albumId),
        albumOrder: prev.albumOrder.filter(id => id !== albumId),
        lastOpened,
        pinned,
      };
    });
    toast.success("Removed from your albums");
  };

  // Pins made in another tab or on another device show up here without a refresh.
  watchPinned = (ref) => {
    this.unwatchPinned();
    const onValue = (snap) => {
      const pinned = snap.val() || {};
      this.setState(prev => ({ pinned, albumOrder: arrangeAlbumIds(prev.albumOrder, pinned) }));
    };
    ref.on('value', onValue);
    this.unwatchPinned = () => { ref.off('value', onValue); this.unwatchPinned = () => {}; };
  };

  unwatchPinned = () => {};

  // While a song's ⋮ menu is open, a click anywhere else (or Escape) closes it. Clicks on the menu itself
  // and on any ⋮ trigger are left alone: the trigger's own handler opens, switches or closes the menu.
  watchMenuDismiss = () => {
    if (this.stopMenuDismiss) return;
    const close = () => this.setState({ menuOpenIndex: null });
    const onMouseDown = (e) => { if (!(e.target.closest && e.target.closest('[data-song-menu]'))) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    this.stopMenuDismiss = () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      this.stopMenuDismiss = null;
    };
  };

  componentWillUnmount() {
    if (this.stopMenuDismiss) this.stopMenuDismiss();
    this.unwatchPinned();
    if (this.unsubscribeExperiments) this.unsubscribeExperiments();
    if (this.unsubscribePresence) this.unsubscribePresence();
  }

  // Opening an album records it and moves it to the top of the unpinned albums right away.
  markAlbumOpened = (albumId) => {
    const time = Date.now();
    this.setState(prev => ({
      lastOpened: { ...prev.lastOpened, [albumId]: time },
      albumOrder: arrangeAlbumIds(prev.albumOrder, prev.pinned, albumId),
    }));
    this.db.child('users').child(this.state.userId).child('albumOpened').child(albumId).set(time).catch(() => {});
  };

  // Pinning moves the album straight into (or out of) the pinned group at the top.
  setPinned = (albumId, pinnedAt) => {
    this.setState(prev => {
      const pinned = { ...prev.pinned };
      if (pinnedAt) pinned[albumId] = pinnedAt; else delete pinned[albumId];
      return { pinned, albumOrder: arrangeAlbumIds(prev.albumOrder, pinned, albumId) };
    });
  };

  togglePinned = async (albumId, e) => {
    e.stopPropagation();
    const was = this.state.pinned[albumId];
    const pinnedAt = was ? null : Date.now();
    this.setPinned(albumId, pinnedAt);
    try {
      await this.db.child('users').child(this.state.userId).child('pinnedAlbums').child(albumId).set(pinnedAt);
    } catch (err) {
      this.setPinned(albumId, was || null);
      toast.error("Couldn't update pin ❌");
    }
  };

  setAlbumPrefs = (patch) => {
    this.setState(patch, () => {
      try {
        window.localStorage.setItem(ALBUM_PREFS_KEY, JSON.stringify({ albumSort: this.state.albumSort, showPinned: this.state.showPinned }));
      } catch (e) { /* storage unavailable; the choice just won't be remembered */ }
    });
  };

  // The albums in sidebar order. "Recently played" is the live order kept in `albumOrder` (pinned
  // first, then most recently opened); "Alphabetical" sorts by name, still with pinned first. With
  // "Show pinned" off, pins are ignored altogether, so they don't reorder anything unseen.
  getSidebarAlbums = () => {
    const { userAlbums, albumOrder, lastOpened, pinned, albumSort, showPinned } = this.state;
    const byId = {};
    userAlbums.forEach(a => { byId[a.id] = a; });
    if (albumSort === 'alpha') {
      const sorted = [...userAlbums].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
      return showPinned ? [...sorted.filter(a => pinned[a.id]), ...sorted.filter(a => !pinned[a.id])] : sorted;
    }
    if (!showPinned) return sortAlbumIds(userAlbums, lastOpened, {}).map(id => byId[id]);
    return [
      ...albumOrder.map(id => byId[id]).filter(Boolean),
      ...userAlbums.filter(a => !albumOrder.includes(a.id)),
    ];
  };

  // Pages that live inside the app shell share the player; picking something to browse goes back to /user.
  leaveProfile = () => {
    if (this.props.location && this.props.location.pathname !== '/user') this.props.history.push('/user');
  };

  openAlbum = (albumID, playSongId) => {
    this.markAlbumOpened(albumID);
    this.leaveProfile();
    this.loadAlbum(albumID, playSongId);
  };

  // The player's cover / album title: jump to the album being played. Albums outside the user's
  // library (e.g. from a saved song) open too, but aren't recorded as "recently opened".
  openPlayingAlbum = () => {
    const item = this.state.queue[this.state.queueIndex];
    if (!item || !item.albumId) return;
    if (this.state.userAlbums.some(a => a.id === item.albumId)) {
      this.openAlbum(item.albumId);
    } else {
      this.leaveProfile();
      this.loadAlbum(item.albumId);
    }
  };

  // The player's artist name: the artist is the album owner's profile (as on the album page).
  openPlayingArtist = async (username) => {
    const item = this.state.queue[this.state.queueIndex];
    if (!item || !item.albumId) return;
    try {
      const ownerId = username || (await this.db.child('albums').child(item.albumId).child('userId').once('value')).val();
      if (!ownerId) throw new Error('no owner');
      this.props.history.push(`/u/${encodeURIComponent(ownerId)}`);
    } catch (e) {
      toast.error("Couldn't open profile ❌");
    }
  };

  openPlaylist = (playlistId) => {
    this.leaveProfile();
    this.loadPlaylist(playlistId);
  };

  // Opens an album; with `playSongId`, starts that song once the album has loaded.
  // Whatever is playing keeps playing: the player has its own queue.
  loadAlbum = (albumID, playSongId) => {
    trackScreen(SCREENS.album);
    this.setState({
      searchQuery: '',
      selectedAlbumId: albumID,
      selectedPlaylistId: '',
      playlistLoading: false,
      albumName: '',
      artistName: '',
      albumOwnerId: '',
      albumOwners: [],
      albumCollaborators: [],
      albumDescription: '',
      albumLinks: {},
      albumStatus: DEFAULT_STATUS,
      albumUrl: '',
      songs: [],
      songNames: [],
      songURLs: [],
      songIndexes: [],
      songMeta: [],
      songVersions: [],
      editMode: false,
      analyticsMode: false,
      songListens: {},
      menuOpenIndex: null,
    });

    this.db.child('albums').child(albumID).once('value', async snap => {
      if (this.state.selectedAlbumId !== albumID) return;
      if (!snap.exists()) { toast.error("This album isn't available ❌"); return; }

      this.setState({
        artistName: snap.val().artist,
        albumOwnerId: snap.val().userId || '',
        albumOwners: this.ownersOf(snap.val()),
        albumCollaborators: Object.keys(snap.val().collaborators || {}),
        albumDescription: snap.val().description || '',
        albumLinks: readAlbumLinks(snap.val()),
        albumName: snap.val().name,
        albumStatus: snap.val().status || DEFAULT_STATUS,
        albumUrl: snap.val().albumCoverURL,
      });
      this.resolveArtists(albumID, snap.val());

      const songEntries = [];
      snap.child("songs").forEach(child => {
        songEntries.push({ key: child.key, index: child.val() });
      });

      songEntries.sort((a, b) => a.index - b.index);

      const loaded = (await Promise.all(songEntries.map(({ key, index }) =>
        this.db.child('songs').child(key).once('value').then(songSnap => ({ key, index, song: songSnap.val() }))
      ))).filter(({ song }) => song);

      // Ignore the result if another album was selected while loading.
      if (this.state.selectedAlbumId !== albumID) return;
      this.setState({
        songs: loaded.map(({ key }) => key),
        songNames: loaded.map(({ song }) => song.name),
        songURLs: loaded.map(({ song }) => songAudioUrl(song)),
        songIndexes: loaded.map(({ index }) => index),
        songVersions: loaded.map(({ song }) => albumVersionInfo(song.versions, song.defaultVersion || 'v1')),
        songArtistKeys: this.artistKeysBySong(loaded.map(({ key, song }) => ({ id: key, song }))),
      }, () => {
        const queue = this.listSnapshot();
        const i = queue.findIndex(item => item.id === playSongId && item.versionId === null);
        if (playSongId && i >= 0) this.startQueue(queue, i);
      });
    });
  };

  artistKeysBySong = (entries) => {
    const keys = {};
    entries.forEach(({ id, song }) => { const k = Object.keys((song && song.artists) || {}); if (k.length) keys[id] = k; });
    return keys;
  };

  // Lists the user's saved songs (newest first), mirroring the iOS PlaylistViewController.
  // The song/audio state is shared with the album view so the player works unchanged.
  loadPlaylist = async (playlistId) => {
    const session = getSession();
    if (!session) return;
    this.setState({
      searchQuery: '',
      selectedPlaylistId: playlistId,
      playlistLoading: true,
      selectedAlbumId: '',
      albumName: '',
      artistName: '',
      albumUrl: '',
      songs: [],
      songNames: [],
      songURLs: [],
      songIndexes: [],
      songMeta: [],
      songVersions: [],
      editMode: false,
      analyticsMode: false,
      songListens: {},
      menuOpenIndex: null,
    });

    try {
      const saved = await this.db.child('playlists').child(session.username).once('value');
      const entries = [];
      saved.forEach(child => {
        entries.push({ id: child.key, date: child.val()?.date || '', albumId: child.val()?.albumID || '' });
      });

      const albums = {};
      const resolvedAlbums = new Set();
      const getAlbum = (id) => albums[id] || (albums[id] = this.db.child('albums').child(id).once('value').then(s => s.val()));

      // Entries whose song or album no longer exists are skipped, as on iOS.
      const loaded = (await Promise.all(entries.map(async ({ id, date, albumId }) => {
        const song = (await this.db.child('songs').child(id).once('value')).val();
        const album = song && albumId ? await getAlbum(albumId) : null;
        if (!song || !album) return null;
        if (!resolvedAlbums.has(albumId)) { resolvedAlbums.add(albumId); this.resolveArtists(albumId, album); }
        return {
          id,
          song,
          time: Date.parse(date) || 0,
          name: song.name || "",
          url: songAudioUrl(song),
          meta: { albumId, albumTitle: album.name || "", artist: album.artist || "", coverUrl: album.albumCoverURL || "" },
        };
      }))).filter(Boolean).sort((a, b) => b.time - a.time);

      if (this.state.selectedPlaylistId !== playlistId) return;
      this.setState({
        songs: loaded.map(s => s.id),
        songNames: loaded.map(s => s.name),
        songURLs: loaded.map(s => s.url),
        songIndexes: loaded.map((_, i) => i),
        songMeta: loaded.map(s => s.meta),
        songVersions: [],
        songArtistKeys: this.artistKeysBySong(loaded),
        playlistLoading: false,
      });
    } catch (e) {
      if (this.state.selectedPlaylistId !== playlistId) return;
      this.setState({ playlistLoading: false });
      toast.error("Couldn't load saved songs ❌");
    }
  };

  // 🔍 Search covers the logged-in user's own library: their albums, those albums' artists and songs.
  // Album and artist matches come straight from the sidebar data; songs need an index of every
  // song in those albums, fetched on first use.
  ensureSongIndex = async () => {
    if (this.state.userAlbums.length === 0) return;
    if (this.songIndexPromise && Date.now() - this.songIndexAt < 30000) return;
    this.songIndexAt = Date.now();
    const promise = (async () => {
      const lists = await Promise.all(this.state.userAlbums.map(album =>
        this.db.child('albums').child(album.id).child('songs').once('value')
          .then(snap => Object.keys(snap.val() || {}).map(id => ({ id, albumId: album.id })))
      ));
      const entries = await Promise.all(lists.flat().map(({ id, albumId }) =>
        this.db.child('songs').child(id).once('value').then(snap => ({ id, albumId, song: snap.val() }))
      ));
      return entries.filter(({ song }) => song).map(({ id, albumId, song }) => ({ id, albumId, name: song.name || "" }));
    })();
    this.songIndexPromise = promise;
    this.setState({ songIndexLoading: true });
    try {
      const songIndex = await promise;
      if (this.songIndexPromise === promise) this.setState({ songIndex, songIndexLoading: false });
    } catch (e) {
      if (this.songIndexPromise === promise) {
        this.songIndexPromise = null;
        this.setState({ songIndexLoading: false });
        toast.error("Couldn't load songs for search ❌");
      }
    }
  };

  // Makes the next search rebuild the song index (call after songs are added, renamed or removed).
  invalidateSongIndex = () => {
    this.songIndexAt = 0;
  };

  focusSearch = () => {
    this.setState({ sidebarCollapsed: false }, () => {
      if (this.searchInputRef.current) this.searchInputRef.current.focus();
    });
  };

  getSearchResults = () => {
    const tokens = normalizeText(this.state.searchQuery).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return null;
    const matches = (text) => {
      const normalized = normalizeText(text);
      return tokens.every(token => normalized.includes(token));
    };
    const prefixFirst = (text) => (normalizeText(text).startsWith(tokens[0]) ? 0 : 1);

    const { userAlbums, songIndex } = this.state;
    const albums = userAlbums
      .filter(a => matches(a.name) || matches(this.artistLine(a.id, a.artist)))
      .sort((a, b) => prefixFirst(a.name) - prefixFirst(b.name));

    const artistsByName = new Map();
    userAlbums.forEach(a => {
      this.artistsOf(a.id, a.artist).forEach(({ name }) => {
        const key = normalizeText(name).trim();
        if (!key || !matches(name)) return;
        const artist = artistsByName.get(key) || { name, albumCount: 0 };
        artist.albumCount += 1;
        artistsByName.set(key, artist);
      });
    });
    const artists = [...artistsByName.values()].sort((a, b) => prefixFirst(a.name) - prefixFirst(b.name));

    const albumsById = {};
    userAlbums.forEach(a => { albumsById[a.id] = a; });
    const songs = songIndex
      .filter(s => matches(s.name) && albumsById[s.albumId])
      .sort((a, b) => prefixFirst(a.name) - prefixFirst(b.name))
      .map(s => ({ ...s, album: albumsById[s.albumId] }));

    return { albums, artists, songs };
  };

  // ✏️ Editing
  toggleEditMode = () => {
    this.setState(prev => ({ editMode: !prev.editMode, analyticsMode: false, menuOpenIndex: null }));
  };

  // Play counts (songs/<id>/listens, bumped by the iOS app) for the album's songs. Collaborators only.
  // Read fresh each time it's switched on so the numbers aren't stale.
  toggleAnalytics = async () => {
    if (this.state.analyticsMode) {
      this.setState({ analyticsMode: false });
      return;
    }
    if (!this.canContribute()) return;
    const albumId = this.state.selectedAlbumId;
    this.setState({ analyticsMode: true, menuOpenIndex: null });
    try {
      const entries = await Promise.all(this.state.songs.map(async id => {
        const n = (await this.db.child('songs').child(id).child('listens').once('value')).val();
        return [id, Number(n) || 0];
      }));
      if (this.state.selectedAlbumId !== albumId) return;
      this.setState({ songListens: Object.fromEntries(entries) });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load play data");
    }
  };

  // Same storage path and DB field as the iOS app.
  uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please choose an image file");
      return;
    }
    const albumId = this.state.selectedAlbumId;
    this.setState({ uploadingCover: true });
    try {
      // A GIF is stored as .gif so anything reading the link by its extension (like Discord's image
      // proxy) treats it as animated; the other kind of cover is removed so it isn't left behind.
      const isGif = file.type === 'image/gif';
      const ref = this.storage.child(`images/${albumId}.${isGif ? 'gif' : 'jpg'}`);
      await ref.put(file, { contentType: file.type });
      const url = await ref.getDownloadURL();
      this.storage.child(`images/${albumId}.${isGif ? 'jpg' : 'gif'}`).delete().catch(() => {});
      await this.db.child('albums').child(albumId).child('albumCoverURL').set(url);
      this.setState(prev => ({
        albumUrl: prev.selectedAlbumId === albumId ? url : prev.albumUrl,
        coverBust: `&t=${Date.now()}`,
        userAlbums: prev.userAlbums.map(a => a.id === albumId ? { ...a, coverUrl: url } : a),
      }));
      toast.success("Cover updated ✅");
      this.setState({ uploadingCover: false });
      return true;
    } catch (e) {
      toast.error("Couldn't upload cover ❌");
    }
    this.setState({ uploadingCover: false });
    return false;
  };

  // A picked cover. The cropper redraws the image as a still JPEG, which would flatten a GIF, so a GIF
  // is uploaded as it is (shown square, cropped to fill, like any other cover).
  pickCover = async (file) => {
    if (!file) return;
    if (file.type === 'image/gif') {
      if (file.size > MAX_GIF_COVER_BYTES) {
        toast.error(`That GIF is too big (max ${MAX_GIF_COVER_BYTES / 1024 / 1024} MB)`);
        return;
      }
      // Any size works as long as it's square: the GIF header holds its width and height.
      const header = new DataView(await file.slice(0, 10).arrayBuffer());
      const width = header.byteLength >= 10 ? header.getUint16(6, true) : 0;
      const height = header.byteLength >= 10 ? header.getUint16(8, true) : 0;
      if (!width || width !== height) {
        toast.error(`GIF covers need to be square (1:1). This one is ${width} × ${height}`);
        return;
      }
      this.uploadCover(file);
      return;
    }
    this.setState({ cropFile: file });
  };

  saveCover = async (blob) => {
    if (await this.uploadCover(blob)) this.setState({ cropFile: null });
  };

  openPrompt = (kind) => {
    this.setState({ prompt: { kind, value: '' } });
  };

  closePrompt = () => {
    this.setState({ prompt: null });
  };

  submitPrompt = (e) => {
    e.preventDefault();
    const { kind, value } = this.state.prompt;
    const name = value.trim();
    if (!name) return;
    this.closePrompt();
    if (kind === 'album') this.createAlbum(name);
    else this.createSong(name);
  };

  createAlbum = async (name) => {
    const session = getSession();
    if (!session) return;
    const albumRef = this.db.child('albums').push();
    const id = albumRef.key;
    const artist = session.name || session.username;
    try {
      await albumRef.set({ name, artist, userId: session.username, status: DEFAULT_STATUS });
      await this.db.child('users').child(session.username).child('albums').child(id).set(true);
    } catch (e) {
      toast.error("Couldn't create album ❌");
      return;
    }
    this.setState(prev => ({
      userAlbums: [...prev.userAlbums, { id, name, coverUrl: null, artist, status: DEFAULT_STATUS }],
      albumOrder: arrangeAlbumIds(prev.albumOrder, prev.pinned, id),
    }));
    this.openAlbum(id);
    toast.success("Album created ✅");
  };

  createSong = async (name) => {
    const albumId = this.state.selectedAlbumId;
    if (!albumId || !this.canContribute()) return;
    const index = Math.max(-1, ...this.state.songIndexes) + 1;
    const songRef = this.db.child('songs').push();
    try {
      await songRef.set({ name, listens: 0 });
      await this.db.child('albums').child(albumId).child('songs').child(songRef.key).set(index);
    } catch (e) {
      toast.error("Couldn't add song ❌");
      return;
    }
    this.invalidateSongIndex();
    if (this.state.selectedAlbumId !== albumId) return;
    this.setState(prev => ({
      songs: [...prev.songs, songRef.key],
      songNames: [...prev.songNames, name],
      songURLs: [...prev.songURLs, ""],
      songIndexes: [...prev.songIndexes, index],
      songVersions: [...prev.songVersions, albumVersionInfo({}, 'v1')],
    }));
  };

  // Called from an input's onBlur; reverts the input if the new value is empty or unchanged.
  saveAlbumField = async (field, input) => {
    const stateKey = { name: 'albumName', status: 'albumStatus' }[field];
    const value = input.value.trim();
    const albumId = this.state.selectedAlbumId;
    if (!value || value === this.state[stateKey]) {
      input.value = this.state[stateKey];
      return;
    }
    try {
      await this.db.child('albums').child(albumId).child(field).set(value);
    } catch (e) {
      input.value = this.state[stateKey];
      toast.error("Couldn't save ❌");
      return;
    }
    this.setState(prev => ({
      [stateKey]: prev.selectedAlbumId === albumId ? value : prev[stateKey],
      userAlbums: prev.userAlbums.map(a =>
        a.id === albumId ? { ...a, [field]: value } : a),
    }));
  };

  // Called from a link input's onBlur. An empty box removes the link; anything else must be a link on
  // that service. Stores the cleaned-up https URL at albums/<id>/links/<service>.
  saveAlbumLink = async (service, input) => {
    const albumId = this.state.selectedAlbumId;
    const stored = this.state.albumLinks[service.id] || '';
    const typed = input.value.trim();
    if (typed === stored) return;
    let value = '';
    if (typed) {
      value = service.parse(typed);
      if (!value) {
        toast.error(service.error);
        input.value = stored;
        return;
      }
    }
    try {
      await this.db.child('albums').child(albumId).child('links').child(service.id).set(value || null);
    } catch (e) {
      input.value = stored;
      toast.error("Couldn't save ❌");
      return;
    }
    input.value = value;
    this.setState(prev => prev.selectedAlbumId === albumId
      ? { albumLinks: { ...prev.albumLinks, [service.id]: value } } : null);
  };

  // The storage paths of the photos/videos a description embeds (<albumId>/media/<id>).
  mediaPaths = (albumId, text) => {
    const paths = new Set();
    const re = /https:\/\/firebasestorage\.googleapis\.com(?::443)?\/[^\s)"']*\/o\/([^?\s)"']+)/g;
    let m;
    while ((m = re.exec(text || '')) !== null) {
      try {
        const path = decodeURIComponent(m[1]);
        if (path.startsWith(`${albumId}/media/`)) paths.add(path);
      } catch (e) { /* not a path we made */ }
    }
    return paths;
  };

  // About this album: a Markdown document at albums/<id>/description. Called by the editor with the
  // album it was editing, which may no longer be the one on screen. Files the document no longer
  // embeds (removed from the text, or uploaded and then dropped) are deleted from storage.
  saveAlbumDescription = async (albumId, text, previous) => {
    const value = text.replace(/\s+$/, '');
    if (previous !== value) {
      try {
        await this.db.child('albums').child(albumId).child('description').set(value || null);
      } catch (e) {
        toast.error("Couldn't save ❌");
        return;
      }
      this.setState(prev => prev.selectedAlbumId === albumId ? { albumDescription: value } : null);
    }
    const pending = this.pendingMedia[albumId] || (this.pendingMedia[albumId] = new Set());
    const keep = this.mediaPaths(albumId, value);
    const stale = [...this.mediaPaths(albumId, previous), ...pending].filter(path => !keep.has(path));
    keep.forEach(path => pending.delete(path));
    stale.forEach(path => {
      pending.delete(path);
      this.storage.child(path).delete().catch(() => {});
    });
  };

  // Photos and videos embedded in the document live in storage under <albumId>/media/<id>.
  uploadAlbumMedia = async (albumId, file) => {
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const path = `${albumId}/media/${id}`;
    const ref = this.storage.child(path);
    await ref.put(file, { contentType: file.type });
    (this.pendingMedia[albumId] || (this.pendingMedia[albumId] = new Set())).add(path);
    return { url: await ref.getDownloadURL(), kind: file.type.startsWith('video/') ? 'video' : 'image' };
  };

  renameSong = async (index, input) => {
    const value = input.value.trim();
    const songId = this.state.songs[index];
    if (!value || value === this.state.songNames[index]) {
      input.value = this.state.songNames[index];
      return;
    }
    try {
      await this.db.child('songs').child(songId).child('name').set(value);
    } catch (e) {
      input.value = this.state.songNames[index];
      toast.error("Couldn't save ❌");
      return;
    }
    this.invalidateSongIndex();
    this.setState(prev => {
      const i = prev.songs.indexOf(songId);
      if (i < 0) return null;
      const songNames = [...prev.songNames];
      songNames[i] = value;
      return { songNames, queue: prev.queue.map(q => q.id === songId ? { ...q, base: value, name: value + q.label } : q) };
    });
  };

  // State for a song list with `songId` taken out. If it's in the queue being played from this
  // list, the queue follows: playback stops if it was the current track.
  dropSong = (prev, songId) => {
    const i = prev.songs.indexOf(songId);
    if (i < 0) return null;
    const without = (arr) => arr.filter((_, n) => n !== i);
    let { queue, queueIndex, isPlaying, isBuffering } = prev;
    // A song can have several rows (one per visible version) in the queue.
    const doomed = queue.map((item, n) => (item.id === songId ? n : -1)).filter(n => n >= 0);
    if (doomed.length && prev.queueSource === this.sourceKey(prev)) {
      if (doomed.includes(queueIndex)) {
        if (this.audioRef.current) this.audioRef.current.pause();
        queue = [];
        queueIndex = null;
        isPlaying = false;
        isBuffering = false;
      } else {
        queue = queue.filter((_, n) => !doomed.includes(n));
        queueIndex -= doomed.filter(n => n < queueIndex).length;
      }
    }
    return {
      songs: without(prev.songs),
      songNames: without(prev.songNames),
      songURLs: without(prev.songURLs),
      songIndexes: without(prev.songIndexes),
      songMeta: without(prev.songMeta),
      songVersions: without(prev.songVersions),
      queue,
      queueIndex,
      isPlaying,
      isBuffering,
      menuOpenIndex: null,
    };
  };

  // Same record as the iOS heart button: playlists/<username>/<songId> = { date, albumID }.
  toggleSaved = async (songId, albumId) => {
    const session = getSession();
    if (!session) return;
    const saving = !this.state.savedIds[songId];
    const setSaved = (value) => this.setState(prev => ({ savedIds: { ...prev.savedIds, [songId]: value } }));
    const ref = this.db.child('playlists').child(session.username).child(songId);
    setSaved(saving);
    try {
      if (saving) {
        await ref.set({ date: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'), albumID: albumId });
      } else {
        await ref.remove();
      }
    } catch (e) {
      setSaved(!saving);
      toast.error(saving ? "Couldn't save song ❌" : "Couldn't remove song ❌");
    }
  };

  // Same path as the iOS heart button: playlists/<username>/<songId>.
  removeSaved = async (index) => {
    const playlistId = this.state.selectedPlaylistId;
    const songId = this.state.songs[index];
    const session = getSession();
    if (!session) return;
    try {
      await this.db.child('playlists').child(session.username).child(songId).remove();
    } catch (e) {
      toast.error("Couldn't remove song ❌");
      return;
    }
    this.setState(prev => ({
      ...(prev.selectedPlaylistId !== playlistId ? {} : this.dropSong(prev, songId)),
      savedIds: { ...prev.savedIds, [songId]: false },
    }));
  };

  deleteSong = async (index) => {
    const albumId = this.state.selectedAlbumId;
    const songId = this.state.songs[index];
    if (!window.confirm(`Delete "${this.state.songNames[index]}"? This can't be undone.`)) return;

    try {
      // Remove stored audio (versions plus the legacy flat file); missing files are fine.
      const versions = (await this.db.child('songs').child(songId).child('versions').once('value')).val() || {};
      const paths = [`${albumId}/${songId}`, ...Object.keys(versions).map(v => `${albumId}/${songId}/${v}`)];
      await Promise.all(paths.map(path => this.storage.child(path).delete().catch(() => {})));
      await this.db.child('songs').child(songId).remove();
      await this.db.child('albums').child(albumId).child('songs').child(songId).remove();
    } catch (e) {
      toast.error("Couldn't delete song ❌");
      return;
    }

    this.invalidateSongIndex();
    this.setState(prev => prev.selectedAlbumId !== albumId ? null : this.dropSong(prev, songId));
  };

  formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Identifies the list shown (an album or a playlist) so edits know whether the queue came from it.
  sourceKey = (state) => state.selectedAlbumId || (state.selectedPlaylistId ? `playlist:${state.selectedPlaylistId}` : '');

  // The shown list as player queue entries: one per row, so every visible version is playable.
  listSnapshot = () => buildRows(this.state, true).map(({ songIndex, number, ...item }) => item);

  // Starts `queue[index]` from the beginning. The queue then drives prev/next and the player title.
  startQueue = (queue, index) => {
    const item = queue[index];
    if (!item) return;
    if (!item.url) {
      toast.error("No file uploaded for this song ❌");
      return;
    }
    const audio = this.audioRef.current;
    // Loop applies to one song at a time: starting a different song clears it.
    audio.loop = false;
    audio.src = item.url;
    this.loadedSrc = item.url;
    this.listenPending = item.id; // counted once it has played for 5 seconds (see handleTimeUpdate)
    this.playAudio();
    this.setState({
      queue,
      queueIndex: index,
      queueSource: this.sourceKey(this.state),
      isPlaying: true,
      isBuffering: true,
      isLooping: false,
    });
  };

  // The browser rejected the file. Apple Lossless (ALAC) .m4a uploads are the usual cause: Chrome and Firefox
  // can't decode them, so decode in JavaScript instead (src/alac.js) and play a WAV copy. loadedSrc keeps the
  // song's own URL, so resuming doesn't reload it. Anything else is a real failure.
  handleAudioError = () => {
    const audio = this.audioRef.current;
    const src = audio && audio.getAttribute('src');
    if (!src) return;
    const fail = () => {
      this.setState({ isBuffering: false, isPlaying: false });
      toast.error("Couldn't load song ❌");
    };
    if (src.startsWith('blob:') || this.alacFailed === src) {
      fail();
      return;
    }
    this.setState({ isBuffering: true });
    const stale = () => this.loadedSrc !== src; // skipped to another song meanwhile
    alacPlayableUrl(src, stale).then((url) => {
      if (!url || stale()) return;
      audio.src = url;
      if (this.state.isPlaying) this.playAudio();
    }).catch(() => {
      if (stale()) return;
      this.alacFailed = src;
      fail();
    });
  };

  // Browsers can refuse playback that doesn't follow directly from a click (e.g. autoplay after a search).
  playAudio = () => {
    this.audioRef.current.play().catch((e) => {
      if (e.name === 'NotAllowedError') this.setState({ isPlaying: false, isBuffering: false });
    });
  };

  // Pauses or resumes the current track.
  togglePlayback = () => {
    const item = this.state.queue[this.state.queueIndex];
    if (!item) return;
    const audio = this.audioRef.current;
    if (this.state.isPlaying) {
      audio.pause();
      this.setState({ isPlaying: false });
      return;
    }
    // Only (re)load the file when it changed; resuming keeps the buffered audio and the position
    // instead of downloading it again.
    if (this.loadedSrc !== item.url) {
      audio.src = item.url;
      this.loadedSrc = item.url;
      this.setState({ isBuffering: true });
    }
    this.playAudio();
    this.setState({ isPlaying: true });
  };

  // A tap on a row in the shown list.
  playSong = (index) => {
    const row = buildRows(this.state, true)[index];
    if (!row) return;
    if (!row.url) {
      toast.error("No file uploaded for this song ❌");
      return;
    }
    const current = this.state.queue[this.state.queueIndex];
    if (current && current.id === row.id && current.url === row.url) {
      this.togglePlayback();
    } else {
      this.startQueue(this.listSnapshot(), index);
    }
  };

  // Steps through the queue by `step`, wrapping around and skipping songs without a file.
  playRelative = (step) => {
    const { queue, queueIndex } = this.state;
    if (queue.length === 0 || queueIndex === null) return;
    for (let n = 1; n <= queue.length; n++) {
      const index = (queueIndex + step * n % queue.length + queue.length) % queue.length;
      if (queue[index].url) {
        this.startQueue(queue, index);
        return;
      }
    }
  };

  playNext = () => this.playRelative(1);

  playPrev = () => this.playRelative(-1);

  // ↕ Reordering songs in the open album (edit mode). Stored as albums/<id>/songs/<songId> = index, as on iOS.
  moveSong = async (from, to) => {
    const albumId = this.state.selectedAlbumId;
    const count = this.state.songs.length;
    if (!albumId || from === to || from < 0 || to < 0 || from >= count || to >= count) return;
    const before = {
      songs: this.state.songs,
      songNames: this.state.songNames,
      songURLs: this.state.songURLs,
      songIndexes: this.state.songIndexes,
      songVersions: this.state.songVersions,
    };
    const move = (arr) => {
      const next = [...arr];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    };
    const songs = move(before.songs);
    this.setState(prev => {
      // Keep the queue in step when it's being played from this album.
      let { queue, queueIndex } = prev;
      const moved = {
        ...prev,
        songs,
        songNames: move(before.songNames),
        songURLs: move(before.songURLs),
        songVersions: move(before.songVersions),
      };
      if (prev.queueSource === albumId && queueIndex !== null) {
        const playing = queue[queueIndex];
        queue = buildRows(moved, true).map(({ songIndex, number, ...item }) => item);
        queueIndex = playing ? queue.findIndex(q => q.id === playing.id && q.versionId === playing.versionId) : -1;
      }
      return {
        songs,
        songNames: moved.songNames,
        songURLs: moved.songURLs,
        songVersions: moved.songVersions,
        songIndexes: songs.map((_, i) => i),
        queue,
        queueIndex: queueIndex < 0 ? null : queueIndex,
        menuOpenIndex: null,
      };
    });

    const updates = {};
    songs.forEach((id, i) => { updates[id] = i; });
    try {
      await this.db.child('albums').child(albumId).child('songs').update(updates);
    } catch (e) {
      toast.error("Couldn't reorder songs ❌");
      if (this.state.selectedAlbumId === albumId) this.setState(before);
    }
  };

  // ➡️ Moving a song to another of the user's albums: the song joins the end of the target's
  // `songs` list and leaves this one. The song record and its audio files stay where they are.
  openMove = (index) => {
    this.setState({
      menuOpenIndex: null,
      moveDialog: { songId: this.state.songs[index], name: this.state.songNames[index], busy: false },
    });
  };

  closeMove = () => {
    if (this.state.moveDialog && this.state.moveDialog.busy) return;
    this.setState({ moveDialog: null });
  };

  moveSongToAlbum = async (target) => {
    const dialog = this.state.moveDialog;
    const fromId = this.state.selectedAlbumId;
    if (!dialog || dialog.busy || !fromId || target.id === fromId) return;
    const { songId, name } = dialog;
    this.setState({ moveDialog: { ...dialog, busy: true } });
    try {
      const targetSongs = (await this.db.child('albums').child(target.id).child('songs').once('value')).val() || {};
      const index = Math.max(-1, ...Object.values(targetSongs)) + 1;
      await this.db.child('albums').child(target.id).child('songs').child(songId).set(index);
      await this.db.child('albums').child(fromId).child('songs').child(songId).remove();
      // Its credited artists belonged to the old album.
      await this.db.child('songs').child(songId).child('artists').remove();
    } catch (e) {
      this.setState({ moveDialog: null });
      toast.error("Couldn't move song ❌");
      return;
    }
    // Keep this user's own saved entry pointing at the album the song now lives in.
    const session = getSession();
    if (session) {
      const saved = this.db.child('playlists').child(session.username).child(songId);
      saved.child('albumID').once('value')
        .then(snap => snap.exists() && saved.child('albumID').set(target.id))
        .catch(() => {});
    }
    this.invalidateSongIndex();
    this.setState(prev => ({
      moveDialog: null,
      ...(prev.selectedAlbumId === fromId ? this.dropSong(prev, songId) : {}),
    }));
    toast.success(`Moved "${name}" to ${target.name || 'album'} ✅`);
  };

  // 🔀 Moving a song into another song as a version: all of its versions are added to the target
  // after the target's own (numbered on from its highest), and the song itself is removed. The audio
  // files stay where they are; the target just points at them. The target's default is unchanged.
  openMerge = (index) => {
    this.ensureSongIndex();
    this.setState({
      menuOpenIndex: null,
      mergeDialog: { songId: this.state.songs[index], albumId: this.state.selectedAlbumId, name: this.state.songNames[index], query: '', busy: false },
    });
  };

  closeMerge = () => {
    if (this.state.mergeDialog && this.state.mergeDialog.busy) return;
    this.setState({ mergeDialog: null });
  };

  mergeSongInto = async (target) => {
    const dialog = this.state.mergeDialog;
    if (!dialog || dialog.busy || target.id === dialog.songId) return;
    const { songId, albumId, name } = dialog;
    if (!window.confirm(`Move "${name}" into "${target.name}" as a new version? "${name}" will be removed from its album.`)) return;
    this.setState({ mergeDialog: { ...dialog, busy: true } });

    const now = Date.now() / 1000;
    let merged = null;
    try {
      const [source, dest] = (await Promise.all([songId, target.id].map(id => this.db.child('songs').child(id).once('value')))).map(snap => snap.val());
      if (!source || !dest) throw new Error('song missing');
      // Oldest first; a song from before versioning counts as its single version.
      let moving = toVersionList(source.versions).reverse();
      if (moving.length === 0 && source.url) moving = [{ versionNumber: 1, url: source.url, timestamp: now, description: '' }];
      moving = moving.filter(v => v.url);
      if (moving.length === 0) {
        this.setState({ mergeDialog: null });
        toast.error(`"${name}" has no audio to move ❌`);
        return;
      }

      const updates = {};
      let existing = toVersionList(dest.versions);
      if (existing.length === 0 && dest.url) {
        updates['versions/v1'] = { url: dest.url, timestamp: now, versionNumber: 1, description: '' };
        updates.defaultVersion = 'v1';
        existing = toVersionList({ v1: updates['versions/v1'] });
      }
      const defaultVersion = updates.defaultVersion || dest.defaultVersion || 'v1';
      let number = Math.max(0, ...existing.map(v => v.versionNumber));
      const added = moving.map((v) => {
        number += 1;
        const version = { url: v.url, timestamp: v.timestamp || now, versionNumber: number, description: v.description || `From "${name}"` };
        updates[`versions/v${number}`] = version;
        return { id: `v${number}`, ...version, visible: false };
      });
      if (existing.length === 0) {
        // The target had no audio at all: its first version becomes the default.
        updates.defaultVersion = 'v1';
        updates.url = added[0].url;
      }
      await this.db.child('songs').child(target.id).update(updates);
      merged = { versions: [...added.reverse(), ...existing], defaultVersion: updates.defaultVersion || defaultVersion };

      await this.db.child('songs').child(songId).remove();
      await this.db.child('albums').child(albumId).child('songs').child(songId).remove();
    } catch (e) {
      this.setState({ mergeDialog: null });
      toast.error(merged ? "Moved the versions, but couldn't remove the original song ❌" : "Couldn't move song ❌");
      if (merged) this.invalidateSongIndex();
      return;
    }

    // A heart on the original carries over to the target.
    const session = getSession();
    if (session) {
      const saved = this.db.child('playlists').child(session.username);
      saved.child(songId).once('value').then(async (snap) => {
        if (!snap.exists()) return;
        await saved.child(songId).remove();
        if (!(await saved.child(target.id).once('value')).exists()) {
          await saved.child(target.id).set({ date: snap.val().date || new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'), albumID: target.albumId });
        }
        this.setState(prev => ({ savedIds: { ...prev.savedIds, [songId]: false, [target.id]: true } }));
      }).catch(() => {});
    }

    this.invalidateSongIndex();
    this.setState(prev => ({
      mergeDialog: null,
      ...(prev.selectedAlbumId === albumId ? this.dropSong(prev, songId) : {}),
    }));
    this.syncSongVersions(target.id, merged.versions, merged.defaultVersion);
    toast.success(`Moved "${name}" into "${target.name}" ✅`);
  };

  toggleMenu = (index, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const inTopHalf = rect.top + rect.height / 2 < window.innerHeight / 2;
    this.setState(prev => ({
      menuOpenIndex: prev.menuOpenIndex === index ? null : index,
      menuOpenBelow: inTopHalf,
    }));
  };

  // 🎚 Song versions: same data model as the iOS SongVersionViewController. A song has
  // `versions/<id>` ({url, timestamp, versionNumber, description}) and a `defaultVersion`;
  // the legacy `url` field is kept in step with the default for older clients.
  updateVersions = (songId, patch) => {
    this.setState(prev => {
      const dialog = prev.versionsDialog;
      if (!dialog || dialog.songId !== songId) return null;
      return { versionsDialog: { ...dialog, ...(typeof patch === 'function' ? patch(dialog) : patch) } };
    });
  };

  // Brings the song list (and the player's queue) in step after a song's versions changed: which
  // file is the default, and which versions show in the album view.
  syncSongVersions = (songId, versions, defaultVersion) => {
    this.setState(prev => {
      const i = prev.songs.indexOf(songId);
      if (i < 0) return null;
      const info = albumVersionInfo(versions, defaultVersion);
      const def = versions.find(v => v.id === defaultVersion);
      const songURLs = [...prev.songURLs];
      if (def) songURLs[i] = def.url;
      const songVersions = [...prev.songVersions];
      songVersions[i] = info;
      const queue = prev.queue.map(q => {
        if (q.id !== songId) return q;
        const own = q.versionId && versions.find(v => v.id === q.versionId);
        const number = q.versionId === null ? info.defaultNumber : own ? own.versionNumber : null;
        const label = info.extras.length && number !== null ? ` (v${number})` : '';
        return { ...q, url: q.versionId === null && def ? def.url : q.url, label, name: q.base + label };
      });
      return { songURLs, songVersions, queue };
    });
  };

  openVersions = async (index) => {
    const songId = this.state.songs[index];
    this.setState({
      menuOpenIndex: null,
      versionsDialog: {
        songId,
        albumId: this.state.selectedAlbumId,
        name: this.state.songNames[index],
        loading: true,
        busy: false,
        uploading: null,
        description: '',
        editingId: null,
        defaultVersion: 'v1',
        versions: [],
      },
    });
    try {
      const songRef = this.db.child('songs').child(songId);
      const song = (await songRef.once('value')).val() || {};
      let versions = toVersionList(song.versions);
      let defaultVersion = song.defaultVersion || 'v1';
      // A song uploaded before versioning becomes v1, as the iOS app does when opening this list.
      if (versions.length === 0 && song.url) {
        const v1 = { url: song.url, timestamp: Date.now() / 1000, versionNumber: 1, description: '' };
        await songRef.update({ 'versions/v1': v1, defaultVersion: 'v1' });
        versions = toVersionList({ v1 });
        defaultVersion = 'v1';
      }
      this.updateVersions(songId, { loading: false, versions, defaultVersion });
    } catch (e) {
      this.closeVersions();
      toast.error("Couldn't load versions ❌");
    }
  };

  closeVersions = () => {
    this.setState({ versionsDialog: null });
  };

  pickVersionFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".m4a,.mp3,.wav";
    fileInput.onchange = (e) => this.uploadVersion(e.target.files[0]);
    fileInput.click();
  };

  // Uploads `file` as the next version (v<highest + 1>) and makes it the default, like iOS.
  uploadVersion = async (file) => {
    const dialog = this.state.versionsDialog;
    if (!file || !dialog) return;
    const { songId, albumId, versions } = dialog;
    const versionNumber = Math.max(0, ...versions.map(v => v.versionNumber)) + 1;
    const versionId = `v${versionNumber}`;
    this.updateVersions(songId, { uploading: 0 });
    try {
      const ref = this.storage.child(`${albumId}/${songId}/${versionId}`);
      const task = ref.put(file);
      task.on('state_changed', snap => this.updateVersions(songId, {
        uploading: Math.round(100 * snap.bytesTransferred / snap.totalBytes),
      }));
      await task;
      const url = await ref.getDownloadURL();
      const version = { url, timestamp: Date.now() / 1000, versionNumber, description: dialog.description.trim() };
      await this.db.child('songs').child(songId).update({
        [`versions/${versionId}`]: version,
        defaultVersion: versionId,
        url,
      });
      const nextVersions = [{ id: versionId, ...version }, ...versions];
      this.updateVersions(songId, { uploading: null, description: '', defaultVersion: versionId, versions: nextVersions });
      this.syncSongVersions(songId, nextVersions, versionId);
      toast.success("New version uploaded ✅");
    } catch (e) {
      this.updateVersions(songId, { uploading: null });
      toast.error("Upload failed ❌");
    }
  };

  setDefaultVersion = async (versionId) => {
    const { songId, versions } = this.state.versionsDialog;
    const version = versions.find(v => v.id === versionId);
    if (!version) return;
    this.updateVersions(songId, { busy: true });
    try {
      await this.db.child('songs').child(songId).update({ defaultVersion: versionId, url: version.url });
    } catch (e) {
      this.updateVersions(songId, { busy: false });
      toast.error("Couldn't set default version ❌");
      return;
    }
    this.updateVersions(songId, { busy: false, defaultVersion: versionId });
    this.syncSongVersions(songId, versions, versionId);
    toast.success(`Version ${version.versionNumber} is now the default ✅`);
  };

  deleteVersion = async (versionId) => {
    const { songId, versions, defaultVersion } = this.state.versionsDialog;
    const version = versions.find(v => v.id === versionId);
    // Same rules as iOS: never the default, never the last one.
    if (!version || versionId === defaultVersion || versions.length < 2) return;
    if (!window.confirm(`Delete Version ${version.versionNumber}? This permanently removes it and can't be undone.`)) return;
    this.updateVersions(songId, { busy: true });
    try {
      await this.db.child('songs').child(songId).child('versions').child(versionId).remove();
    } catch (e) {
      this.updateVersions(songId, { busy: false });
      toast.error("Couldn't delete version ❌");
      return;
    }
    // Remove the audio file too, unless another version still points at it. Best effort.
    if (version.url && !versions.some(v => v.id !== versionId && v.url === version.url)) {
      try {
        // Some stored URLs carry an explicit :443, which refFromURL doesn't accept.
        this.storage.storage.refFromURL(version.url.replace('firebasestorage.googleapis.com:443', 'firebasestorage.googleapis.com')).delete().catch(() => {});
      } catch (e) { /* not a Storage URL; nothing to remove */ }
      evictCachedSong(version.url);
    }
    const remaining = versions.filter(v => v.id !== versionId);
    this.updateVersions(songId, { busy: false, versions: remaining });
    this.syncSongVersions(songId, remaining, defaultVersion);
  };

  // Ticks or unticks "Show in album" for a version (the default one always shows).
  toggleVersionVisible = async (versionId) => {
    const { songId, versions, defaultVersion } = this.state.versionsDialog;
    const version = versions.find(v => v.id === versionId);
    if (!version || versionId === defaultVersion) return;
    const visible = !version.visible;
    this.updateVersions(songId, { busy: true });
    try {
      const ref = this.db.child('songs').child(songId).child('versions').child(versionId).child('visible');
      await (visible ? ref.set(true) : ref.remove());
    } catch (e) {
      this.updateVersions(songId, { busy: false });
      toast.error("Couldn't change visibility ❌");
      return;
    }
    const next = versions.map(v => v.id === versionId ? { ...v, visible } : v);
    this.updateVersions(songId, { busy: false, versions: next });
    this.syncSongVersions(songId, next, defaultVersion);
  };

  saveVersionDescription = async (versionId, input) => {
    const { songId, versions } = this.state.versionsDialog;
    const version = versions.find(v => v.id === versionId);
    const value = input.value.trim();
    this.updateVersions(songId, { editingId: null });
    if (!version || value === version.description) return;
    try {
      await this.db.child('songs').child(songId).child('versions').child(versionId).child('description').set(value);
    } catch (e) {
      toast.error("Couldn't save description ❌");
      return;
    }
    this.updateVersions(songId, prev => ({
      versions: prev.versions.map(v => v.id === versionId ? { ...v, description: value } : v),
    }));
  };

  downloadFile = (url) => {
    if (!url) {
      toast.error("No file available ❌");
      return;
    }
    window.open(url, '_blank');
  };

  // Anyone with the link can play it without logging in (see Share.js).
  copyShareLink = async (path) => {
    this.setState({ menuOpenIndex: null });
    const link = siteUrl(path);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied 🔗");
    } catch (e) {
      window.prompt("Copy this link:", link);
    }
  };

  // 🎤 Artists. Album-level, in the Artists dialog: which of the owner, collaborators and artists who
  // aren't on Stew show on the album's artist line (`albums/<id>/hiddenArtists/<key>`), and the
  // not-on-Stew artists themselves (`albums/<id>/externalArtists/<key> = { name }`). Song-level: the
  // artists a song credits (`songs/<id>/artists/<key> = true`); a song without any shows the album's.
  openArtists = () => {
    const albumId = this.state.selectedAlbumId;
    if (!albumId || !this.canEditAlbum()) return;
    this.setState({ menuOpenIndex: null, artistsDialog: { albumId, name: '', query: '', directory: null, bands: null, busy: false } });
    loadUserBands(this.state.userId).catch(() => []).then(bands => this.setState(prev => (
      prev.artistsDialog && prev.artistsDialog.albumId === albumId
        ? { artistsDialog: { ...prev.artistsDialog, bands } } : null)));
    this.loadUserDirectory().then(directory => this.setState(prev => (
      prev.artistsDialog && prev.artistsDialog.albumId === albumId
        ? { artistsDialog: { ...prev.artistsDialog, directory } } : null)))
      .catch(() => {});
  };

  // Puts someone who is on Stew on the album as a collaborator (credited, can add songs and versions,
  // not an owner) and in their own library. Resolves with whether it worked.
  addStewCollaborator = async (albumId, user) => {
    try {
      await this.db.update({
        [`albums/${albumId}/collaborators/${user.username}`]: true,
        [`users/${user.username}/albums/${albumId}`]: true,
      });
    } catch (e) {
      toast.error("Couldn't add artist ❌");
      return false;
    }
    await this.refreshPeople(albumId);
    return true;
  };

  addAlbumStewArtist = async (user) => {
    const d = this.state.artistsDialog;
    if (!d || d.busy || !this.canEditAlbum()) return;
    this.setState({ artistsDialog: { ...d, busy: true } });
    const ok = await this.addStewCollaborator(d.albumId, user);
    this.setState(prev => (prev.artistsDialog
      ? { artistsDialog: { ...prev.artistsDialog, busy: false, query: ok ? '' : prev.artistsDialog.query } } : null));
    if (ok) toast.success(`${user.name || user.username} added as a collaborator ✅`);
  };

  closeArtists = () => this.setState({ artistsDialog: null });

  // Credits one of your bands on the album (`albums/<id>/bands/<bandId> = true`). Resolves with whether it worked.
  addBandToAlbum = async (albumId, band) => {
    try {
      await this.db.child('albums').child(albumId).child('bands').child(band.id).set(true);
    } catch (e) {
      toast.error("Couldn't add band ❌");
      return false;
    }
    await this.refreshArtists(albumId);
    return true;
  };

  addAlbumBand = async (band) => {
    const d = this.state.artistsDialog;
    if (!d || d.busy || !this.canEditAlbum()) return;
    this.setState({ artistsDialog: { ...d, busy: true } });
    const ok = await this.addBandToAlbum(d.albumId, band);
    this.setState(prev => (prev.artistsDialog ? { artistsDialog: { ...prev.artistsDialog, busy: false } } : null));
    if (ok) toast.success(`${band.name} added ✅`);
  };

  // Adds an artist who isn't on Stew to the album. Resolves with the new artist's key.
  addExternalArtist = async (albumId, rawName) => {
    const name = rawName.trim();
    if (!name) return null;
    const existing = (this.state.albumArtists[albumId] || []).find(a => a.name.toLowerCase() === name.toLowerCase());
    if (existing) return existing.key;
    try {
      const ref = this.db.child('albums').child(albumId).child('externalArtists').push();
      await ref.set({ name });
      await this.refreshArtists(albumId);
      return ref.key;
    } catch (e) {
      toast.error("Couldn't add artist ❌");
      return null;
    }
  };

  submitArtist = async (e) => {
    e.preventDefault();
    const d = this.state.artistsDialog;
    if (!d || d.busy || !d.name.trim()) return;
    this.setState({ artistsDialog: { ...d, busy: true } });
    const key = await this.addExternalArtist(d.albumId, d.name);
    this.setState(prev => (prev.artistsDialog ? { artistsDialog: { ...prev.artistsDialog, busy: false, name: key ? '' : prev.artistsDialog.name } } : null));
  };

  // Shows or hides an artist on the album's artist line. At least one stays shown.
  toggleArtistShown = async (artist) => {
    const d = this.state.artistsDialog;
    if (!d || d.busy) return;
    const all = this.state.albumArtists[d.albumId] || [];
    if (!artist.hidden && all.filter(a => !a.hidden).length <= 1) {
      toast.error("Show at least one artist on the album");
      return;
    }
    this.setState({ artistsDialog: { ...d, busy: true } });
    try {
      const ref = this.db.child('albums').child(d.albumId).child('hiddenArtists').child(artist.key);
      await (artist.hidden ? ref.remove() : ref.set(true));
      await this.refreshArtists(d.albumId);
    } catch (err) {
      toast.error("Couldn't save ❌");
    }
    this.setState(prev => (prev.artistsDialog ? { artistsDialog: { ...prev.artistsDialog, busy: false } } : null));
  };

  removeExternalArtist = async (artist) => {
    const d = this.state.artistsDialog;
    if (!d || d.busy || !(artist.external || artist.band)) return;
    this.setState({ artistsDialog: { ...d, busy: true } });
    try {
      const album = this.db.child('albums').child(d.albumId);
      await Promise.all([album.child(artist.band ? 'bands' : 'externalArtists').child(artist.key).remove(), album.child('hiddenArtists').child(artist.key).remove()]);
      await this.refreshArtists(d.albumId);
    } catch (err) {
      toast.error("Couldn't remove artist ❌");
    }
    this.setState(prev => (prev.artistsDialog ? { artistsDialog: { ...prev.artistsDialog, busy: false } } : null));
  };

  openSongArtists = (index) => {
    const songId = this.state.songs[index];
    if (!songId || !this.state.selectedAlbumId || !this.canEditAlbum()) return;
    this.setState({
      menuOpenIndex: null,
      songArtistsDialog: {
        songId, albumId: this.state.selectedAlbumId, name: this.state.songNames[index],
        selected: this.state.songArtistKeys[songId] || [], newName: '', query: '', directory: null, bands: null, busy: false,
      },
    });
    loadUserBands(this.state.userId).catch(() => []).then(bands => this.setState(prev => (
      prev.songArtistsDialog && prev.songArtistsDialog.songId === songId
        ? { songArtistsDialog: { ...prev.songArtistsDialog, bands } } : null)));
    this.loadUserDirectory().then(directory => this.setState(prev => (
      prev.songArtistsDialog && prev.songArtistsDialog.songId === songId
        ? { songArtistsDialog: { ...prev.songArtistsDialog, directory } } : null)))
      .catch(() => {});
  };

  closeSongArtists = () => {
    if (this.state.songArtistsDialog && this.state.songArtistsDialog.busy) return;
    this.setState({ songArtistsDialog: null });
  };

  updateSongArtistsDialog = (patch) => this.setState(prev => (prev.songArtistsDialog ? { songArtistsDialog: { ...prev.songArtistsDialog, ...patch } } : null));

  toggleSongArtist = (key) => {
    const d = this.state.songArtistsDialog;
    if (!d) return;
    this.updateSongArtistsDialog({ selected: d.selected.includes(key) ? d.selected.filter(k => k !== key) : [...d.selected, key] });
  };

  // Adds an artist who isn't on Stew from the song dialog: to the album, and ticked for this song.
  addSongArtist = async (e) => {
    e.preventDefault();
    const d = this.state.songArtistsDialog;
    if (!d || d.busy || !d.newName.trim()) return;
    this.updateSongArtistsDialog({ busy: true });
    const key = await this.addExternalArtist(d.albumId, d.newName);
    this.setState(prev => {
      const cur = prev.songArtistsDialog;
      if (!cur) return null;
      return { songArtistsDialog: { ...cur, busy: false, newName: key ? '' : cur.newName,
        selected: key && !cur.selected.includes(key) ? [...cur.selected, key] : cur.selected } };
    });
  };

  // Credits one of your bands: it joins the album's artists and is ticked for this song.
  addSongBand = async (band) => {
    const d = this.state.songArtistsDialog;
    if (!d || d.busy || !this.canEditAlbum()) return;
    this.updateSongArtistsDialog({ busy: true });
    const ok = await this.addBandToAlbum(d.albumId, band);
    this.setState(prev => {
      const cur = prev.songArtistsDialog;
      if (!cur) return null;
      return { songArtistsDialog: { ...cur, busy: false,
        selected: ok && !cur.selected.includes(band.id) ? [...cur.selected, band.id] : cur.selected } };
    });
  };

  // Credits someone who is on Stew: they join the album as a collaborator (credited, can add songs and
  // versions, not an owner) and are ticked for this song.
  addSongStewArtist = async (user) => {
    const d = this.state.songArtistsDialog;
    if (!d || d.busy || !this.canEditAlbum()) return;
    this.updateSongArtistsDialog({ busy: true });
    const ok = await this.addStewCollaborator(d.albumId, user);
    this.setState(prev => {
      const cur = prev.songArtistsDialog;
      if (!cur) return null;
      return { songArtistsDialog: { ...cur, busy: false, query: ok ? '' : cur.query,
        selected: ok && !cur.selected.includes(user.username) ? [...cur.selected, user.username] : cur.selected } };
    });
  };

  saveSongArtists = async () => {
    const d = this.state.songArtistsDialog;
    if (!d || d.busy) return;
    this.updateSongArtistsDialog({ busy: true });
    const ref = this.db.child('songs').child(d.songId).child('artists');
    try {
      await (d.selected.length
        ? ref.set(d.selected.reduce((all, key) => ({ ...all, [key]: true }), {}))
        : ref.remove());
    } catch (err) {
      this.updateSongArtistsDialog({ busy: false });
      toast.error("Couldn't save ❌");
      return;
    }
    this.setState(prev => {
      const songArtistKeys = { ...prev.songArtistKeys };
      if (d.selected.length) songArtistKeys[d.songId] = d.selected; else delete songArtistKeys[d.songId];
      return {
        songArtistKeys,
        songArtistsDialog: null,
        queue: prev.queue.map(q => q.id === d.songId ? { ...q, artistKeys: d.selected } : q),
      };
    });
  };

  // 🤝 People. Three tiers:
  //  - Owners: full control (edit everything, manage people). Several are allowed. Stored in
  //    `albums/<id>/owners/<username> = ms granted`; `userId` stays the primary owner for the iOS app,
  //    and an album with no `owners` node has just that owner.
  //  - Collaborators: credited artists who are on Stew. They can add songs and versions, and see play data.
  //    Same data as iOS: `albums/<id>/collaborators/<username> = true` + `users/<username>/albums/<id>`.
  //    Owners are written there too, because the iOS app lets every collaborator edit.
  //  - Listeners: everyone else with the link. Nothing is stored.
  ownersOf = (album) => {
    const owners = Object.keys(album.owners || {});
    return owners.length ? owners : (album.userId ? [album.userId] : []);
  };

  isAlbumOwner = () => !!this.state.userId && this.state.albumOwners.includes(this.state.userId);

  isAlbumCollaborator = () => !!this.state.userId && !this.isAlbumOwner()
    && this.state.albumCollaborators.includes(this.state.userId);

  // Owners only: everything about the album itself.
  canEditAlbum = () => this.isAlbumOwner();

  // Owners and collaborators: adding songs and versions, and reading play data.
  canContribute = () => this.isAlbumOwner() || this.isAlbumCollaborator();

  // Every user, for the "add collaborator" search (the iOS app reads the same node). Loaded once.
  loadUserDirectory = () => {
    if (!this.userDirectory) {
      this.userDirectory = this.db.child('users').once('value').then(snap => {
        const users = [];
        snap.forEach(child => { users.push({ username: child.key, name: child.child('name').val() || '' }); });
        return users;
      }).catch((e) => { this.userDirectory = null; throw e; });
    }
    return this.userDirectory;
  };

  updateShare = (patch) => this.setState(prev => (prev.shareDialog ? { shareDialog: { ...prev.shareDialog, ...patch } } : null));

  openShare = () => {
    const albumId = this.state.selectedAlbumId;
    if (!albumId) return;
    this.setState({ menuOpenIndex: null, shareDialog: { albumId, query: '', directory: null, names: {}, busyUser: '' } });
    const stillOpen = () => this.state.shareDialog && this.state.shareDialog.albumId === albumId;
    // Display names for the owner and collaborators (usernames are shown until they arrive).
    [...this.state.albumOwners, ...this.state.albumCollaborators].filter((u, i, all) => u && all.indexOf(u) === i).forEach(username => {
      this.db.child('users').child(username).child('name').once('value').then(snap => {
        if (!stillOpen() || !snap.val()) return;
        this.setState(prev => (prev.shareDialog
          ? { shareDialog: { ...prev.shareDialog, names: { ...prev.shareDialog.names, [username]: snap.val() } } } : null));
      }).catch(() => {});
    });
    if (this.canEditAlbum()) {
      this.loadUserDirectory().then(directory => stillOpen() && this.updateShare({ directory }))
        .catch(() => toast.error("Couldn't load people ❌"));
    }
  };

  closeShare = () => this.setState({ shareDialog: null });

  // Re-reads who is on the open album after a change, so the page matches what was saved.
  refreshPeople = async (albumId) => {
    try {
      const snap = await this.db.child('albums').child(albumId).once('value');
      if (!snap.exists()) return;
      const album = snap.val();
      this.setState(prev => (prev.selectedAlbumId === albumId ? {
        albumOwnerId: album.userId || '',
        albumOwners: this.ownersOf(album),
        albumCollaborators: Object.keys(album.collaborators || {}),
        artistName: album.artist || '',
        userAlbums: prev.userAlbums.map(a => (a.id === albumId ? { ...a, artist: album.artist || '' } : a)),
      } : null));
      this.resolveArtists(albumId, album);
    } catch (e) {
      // The next time the album is opened it loads fresh.
    }
  };

  // Changes the owners map in a transaction, so two owners stepping down at once can't leave the album
  // with none. `change(owners)` edits the map ({ username: ms granted }) and returns false to cancel.
  // Resolves with the saved map, or null if cancelled. Before the server's value is known the map is
  // guessed from the page; if that's wrong the transaction runs again with the real one.
  updateOwners = async (albumId, change) => {
    const guess = {};
    this.state.albumOwners.forEach(u => { guess[u] = 0; });
    const result = await this.db.child('albums').child(albumId).child('owners').transaction((current) => {
      const owners = current ? { ...current } : { ...guess };
      return change(owners) === false ? undefined : owners;
    });
    return result.committed ? result.snapshot.val() : null;
  };

  // When the primary owner (`userId`) stops being an owner, the longest-standing remaining owner takes
  // over, so the iOS app and profile pages keep a valid owner.
  passPrimaryOwner = async (albumId, owners) => {
    const album = (await this.db.child('albums').child(albumId).once('value')).val();
    if (!album || (album.userId && owners[album.userId])) return;
    const next = Object.keys(owners)
      .sort((a, b) => (owners[a] || 0) - (owners[b] || 0) || a.localeCompare(b))[0];
    if (!next) return;
    // The `artist` text isn't shown on the web any more, but the iOS app still reads it, so keep it
    // matching the new primary owner.
    const patch = { userId: next, artist: (await this.getUserName(next)) || next };
    await this.db.child('albums').child(albumId).update(patch);
  };

  // Adds someone as a collaborator (credited, can add songs and versions) or an owner. Owners only.
  addPerson = async (user, role) => {
    const albumId = this.state.selectedAlbumId;
    if (!albumId || !this.canEditAlbum()) return;
    this.updateShare({ busyUser: user.username });
    try {
      if (role === 'owner') {
        await this.updateOwners(albumId, (o) => { if (!o[user.username]) o[user.username] = Date.now(); });
      }
      await this.db.update({
        [`albums/${albumId}/collaborators/${user.username}`]: true,
        [`users/${user.username}/albums/${albumId}`]: true,
      });
    } catch (e) {
      this.updateShare({ busyUser: '' });
      toast.error(role === 'owner' ? "Couldn't add owner ❌" : "Couldn't add collaborator ❌");
      return;
    }
    await this.refreshPeople(albumId);
    this.setState(prev => (prev.shareDialog ? {
      shareDialog: { ...prev.shareDialog, busyUser: '', query: '', names: { ...prev.shareDialog.names, [user.username]: user.name } },
    } : null));
    toast.success(`${user.name || user.username} is now ${role === 'owner' ? 'an owner' : 'a collaborator'} ✅`);
  };

  // Moves someone between tiers: 'owner', 'collaborator' (still credited, no longer an owner) or 'none'
  // (off the album; it stays in their library). Owners only. An album always keeps at least one owner.
  changeRole = async (username, role) => {
    const albumId = this.state.selectedAlbumId;
    if (!albumId || !this.canEditAlbum()) return;
    const wasOwner = this.state.albumOwners.includes(username);
    if (wasOwner && role !== 'owner' && this.state.albumOwners.length < 2) {
      toast.error("An album needs at least one owner");
      return;
    }
    this.updateShare({ busyUser: username });
    try {
      if (role === 'owner') {
        await this.updateOwners(albumId, (o) => { if (!o[username]) o[username] = Date.now(); });
        await this.db.child('albums').child(albumId).child('collaborators').child(username).set(true);
      } else {
        if (wasOwner) {
          const owners = await this.updateOwners(albumId, (o) => {
            delete o[username];
            if (Object.keys(o).length === 0) return false;
            return true;
          });
          if (!owners) {
            this.updateShare({ busyUser: '' });
            toast.error("An album needs at least one owner");
            return;
          }
          await this.passPrimaryOwner(albumId, owners);
        }
        const collaborators = this.db.child('albums').child(albumId).child('collaborators').child(username);
        if (role === 'none') {
          // Off the album: also forget that they were hidden from its artist line.
          await Promise.all([collaborators.remove(), this.db.child('albums').child(albumId).child('hiddenArtists').child(username).remove()]);
        } else {
          await collaborators.set(true);
        }
      }
    } catch (e) {
      this.updateShare({ busyUser: '' });
      toast.error("Couldn't save ❌");
      return;
    }
    await this.refreshPeople(albumId);
    this.updateShare({ busyUser: '' });
    if (username === this.state.userId && !this.isAlbumOwner()) {
      // Stepped down: close anything that needed ownership.
      this.setState({ editMode: false, analyticsMode: false, artistsDialog: null, songArtistsDialog: null, menuOpenIndex: null });
      toast.success("You're no longer an owner of this album");
    }
  };

  // The signed-in person steps off the album's credits (owner or collaborator) but keeps it: it moves to
  // their saved albums (`users/<me>/savedAlbums`) and drops out of `users/<me>/albums`, so they can still
  // open it and, as a listener, can remove it later with the bookmark. The last owner can't leave.
  leaveAlbum = async () => {
    const albumId = this.state.selectedAlbumId;
    const me = this.state.userId;
    if (!albumId || !me || !this.canContribute()) return;
    const wasOwner = this.isAlbumOwner();
    if (wasOwner && this.state.albumOwners.length < 2) {
      toast.error("An album needs at least one owner. Make someone else an owner first.");
      return;
    }
    if (!window.confirm(`Leave "${this.state.albumName}"? You'll no longer be credited or able to add songs. It stays in your albums as a saved album.`)) return;
    this.updateShare({ busyUser: me });
    try {
      if (wasOwner) {
        const owners = await this.updateOwners(albumId, (o) => {
          delete o[me];
          return Object.keys(o).length > 0;
        });
        if (!owners) {
          this.updateShare({ busyUser: '' });
          toast.error("An album needs at least one owner");
          return;
        }
        await this.passPrimaryOwner(albumId, owners);
      }
      await this.db.update({
        [`albums/${albumId}/collaborators/${me}`]: null,
        [`albums/${albumId}/hiddenArtists/${me}`]: null,
        [`users/${me}/albums/${albumId}`]: null,
        [`users/${me}/savedAlbums/${albumId}`]: Date.now(),
      });
    } catch (e) {
      this.updateShare({ busyUser: '' });
      toast.error("Couldn't leave the album ❌");
      return;
    }
    await this.refreshPeople(albumId);
    this.updateShare({ busyUser: '' });
    this.setState({ editMode: false, analyticsMode: false, artistsDialog: null, songArtistsDialog: null, menuOpenIndex: null });
    toast.success("You left the album. It's still in your saved albums");
  };

  // A listen is counted the way the iOS app does it: songs/<id>/listens goes up by one after a song
  // has been playing for 5 seconds. A transaction keeps simultaneous listeners from overwriting each other.
  countListen = (songId) => {
    this.db.child('songs').child(songId).child('listens')
      .transaction(n => (Number(n) || 0) + 1)
      .then(() => {
        // Keep the play data on screen current if it's showing this song.
        this.setState(prev => (prev.songListens[songId] === undefined ? null
          : { songListens: { ...prev.songListens, [songId]: prev.songListens[songId] + 1 } }));
      })
      .catch(e => console.error('Could not count listen', e));
  };

  // Tells the Discord helper (if the listener has it on) what is playing, or that nothing is.
  pushPresence = () => {
    const audio = this.audioRef.current;
    const item = this.state.queue[this.state.queueIndex];
    if (!audio || !item || audio.paused || audio.ended) {
      sendPresence(null);
      return;
    }
    const origin = SITE_ORIGIN; // links in Discord must be public https pages
    sendPresence({
      title: item.name,
      artist: this.artistLine(item.albumId, item.artist, item.artistKeys),
      album: item.albumTitle,
      coverUrl: item.coverUrl,
      songUrl: item.albumId ? `${origin}/s/${item.albumId}/${item.id}` : undefined,
      albumUrl: item.albumId ? `${origin}/a/${item.albumId}` : undefined,
      position: audio.currentTime,
      duration: isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  handleTimeUpdate = () => {
    const audio = this.audioRef.current;
    const playing = this.state.queue[this.state.queueIndex];
    if (this.listenPending && playing && playing.id === this.listenPending && !audio.paused && audio.currentTime >= 5) {
      const songId = this.listenPending;
      this.listenPending = null;
      this.countListen(songId);
    }
    this.setState({
      progress: audio.currentTime,
      duration: audio.duration || 0,
    });
  };

  handleSeek = (e) => {
    const audio = this.audioRef.current;
    const newTime = e.target.value;
    audio.currentTime = newTime;
    this.setState({ progress: newTime });
  };

  toggleLoop = () => {
    const audio = this.audioRef.current;
    audio.loop = !this.state.isLooping;
    this.setState(prev => ({ isLooping: !prev.isLooping }));
  };

  togglePlayer = () => {
    this.setState(prev => ({ playerCollapsed: !prev.playerCollapsed }));
  };

  toggleSidebar = () => {
    this.setState(prev => ({ sidebarCollapsed: !prev.sidebarCollapsed }));
  };

  render() {
    if (this.state.loggedOut) return <Redirect to="/login" />;

    const sidebarWidth = this.state.sidebarCollapsed ? 60 : 220;
    const playlist = PLAYLISTS.find(p => p.id === this.state.selectedPlaylistId);
    const results = this.getSearchResults();
    const MAX_SONG_RESULTS = 50;
    const pathname = this.props.location ? this.props.location.pathname : '';
    const onProfilePage = pathname === '/profile' || pathname.startsWith('/u/');
    const onBandPage = pathname.startsWith('/b/');
    const showProfile = onProfilePage || onBandPage;
    // /u/<name> shows that person's profile; the sidebar "Profile" button only lights up for your own.
    const viewedProfile = pathname.startsWith('/u/') ? decodeURIComponent(pathname.slice(3)) : undefined;
    const onOwnProfile = onProfilePage && (!viewedProfile || viewedProfile === this.state.userId);
    const albumsById = {};
    this.state.userAlbums.forEach(a => { albumsById[a.id] = a; });
    const sidebarAlbums = this.getSidebarAlbums();
    const sidebarUser = this.state.sidebarUser;
    const profileLabel = sidebarUser.name || this.state.userId || 'Profile';
    const nowPlaying = this.state.queue[this.state.queueIndex];
    // The player bar only shows once something has been played.
    const playerHeight = !nowPlaying ? 0 : this.state.playerCollapsed ? 56 : 120;
    const playingAlbum = nowPlaying && albumsById[nowPlaying.albumId];
    const playerCover = nowPlaying && ((playingAlbum && playingAlbum.coverUrl) || nowPlaying.coverUrl);
    const rows = buildRows(this.state, !this.state.editMode);
    const canEdit = this.canEditAlbum();
    const canContribute = this.canContribute();
    const playingTitle = nowPlaying && ((playingAlbum && playingAlbum.name) || nowPlaying.albumTitle);
    const playingArtists = nowPlaying
      ? this.artistsOf(nowPlaying.albumId, (playingAlbum && playingAlbum.artist) || nowPlaying.artist, nowPlaying.artistKeys) : [];
    const playingArtist = playingArtists.map(a => a.name).join(', ');
    // The list the player is working through, so it stays marked while another album is open.
    const playingSource = nowPlaying ? this.state.queueSource : '';
    const sourceBadge = (sourceId) => playingSource === sourceId && (
      <ThumbBadge><NowPlayingIcon playing={this.state.isPlaying} style={{ width: '10px', height: '10px' }} /></ThumbBadge>
    );
    // The "about this album" document. Rendered in the left column (wide windows) and below
    // the page (narrow ones); CSS shows whichever fits.
    const editingExtras = canEdit && this.state.editMode;
    const showPlays = canContribute && this.state.analyticsMode && !playlist;
    const showExtras = !playlist && !!this.state.selectedAlbumId && !!this.state.albumName
      // Nothing to show until it's written; owners and collaborators still see it while editing.
      && (!!this.state.albumDescription || editingExtras);
    const renderExtras = (className) => (
      <AlbumExtras className={className}>
        <h3>About this album</h3>
        <AlbumDetails key={this.state.selectedAlbumId} albumId={this.state.selectedAlbumId}
          value={this.state.albumDescription} editing={editingExtras}
          onSave={this.saveAlbumDescription} onUploadMedia={this.uploadAlbumMedia} />
      </AlbumExtras>
    );

    const coverSize = this.state.playerCollapsed ? 40 : 80;

    return (
      <React.Fragment>
        <div><Toaster position="top-center" toastOptions={{ style: { background: 'var(--toast-bg)', color: 'var(--toast-fg)' } }} /></div>

        {/* Sidebar */}
        <SidebarContainer style={{ width: sidebarWidth + 'px' }}>
          {/* Branding */}
          <SidebarBrand style={this.state.sidebarCollapsed ? undefined : { justifyContent: 'flex-start', textAlign: 'left' }}>
            <span role="img" aria-label="soup">🍲</span>
            {!this.state.sidebarCollapsed && <span>Stew</span>}
          </SidebarBrand>

          {this.state.sidebarCollapsed ? (
            <SidebarToggle onClick={this.focusSearch} aria-label="Search" title="Search" style={{ margin: '8px 0', flex: '0 0 auto' }}>🔍</SidebarToggle>
          ) : (
            <SearchInput
              ref={this.searchInputRef}
              type="search"
              placeholder="Search songs, albums, artists"
              aria-label="Search songs, albums, artists"
              value={this.state.searchQuery}
              onFocus={this.ensureSongIndex}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              onKeyDown={(e) => e.key === 'Escape' && this.setState({ searchQuery: '' })}
            />
          )}

          <SidebarScroll>
          <SidebarHeader>
            {!this.state.sidebarCollapsed && <span>Playlists</span>}
            <SidebarToggle onClick={this.toggleSidebar}
              aria-label={this.state.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={this.state.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <ChevronsIcon dir={this.state.sidebarCollapsed ? 'right' : 'left'} />
            </SidebarToggle>
          </SidebarHeader>

          {PLAYLISTS.map((p) => (
            <AlbumItemRow
              key={p.id}
              active={!showProfile && this.state.selectedPlaylistId === p.id ? "true" : undefined}
              onClick={() => this.openPlaylist(p.id)}
            >
              <ThumbWrap>
                <AlbumThumb as="div" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1db954, #0d6efd)' }}>{p.icon}</AlbumThumb>
                {sourceBadge(`playlist:${p.id}`)}
              </ThumbWrap>
              {!this.state.sidebarCollapsed && (
                <AlbumTextWrapper><AlbumName>{p.name}</AlbumName></AlbumTextWrapper>
              )}
            </AlbumItemRow>
          ))}

          {!this.state.sidebarCollapsed && (
            <SidebarHeader>
              <span>Albums</span>
              <SidebarToggle onClick={() => this.setState({ filterOpen: true })}
                aria-label="Sort and filter albums" title="Sort and filter albums">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ display: 'block' }}>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="7" y1="12" x2="17" y2="12" />
                  <line x1="10" y1="18" x2="14" y2="18" />
                </svg>
              </SidebarToggle>
            </SidebarHeader>
          )}

          {sidebarAlbums.map((album) => {
            const displayArtist = this.artistLine(album.id, album.artist);
            const isScrolling = displayArtist.length > 25;
            return (
              <AlbumItemRow
                key={album.id}
                active={!showProfile && this.state.selectedAlbumId === album.id ? "true" : undefined}
                onClick={() => this.openAlbum(album.id)}
              >
                <ThumbWrap>
                  <AlbumThumb
                    src={album.coverUrl || coverPlaceholder(album.name)}
                    alt={album.name}
                  />
                  {sourceBadge(album.id)}
                </ThumbWrap>
                {!this.state.sidebarCollapsed && (
                  <AlbumTextWrapper>
                    <AlbumName style={playingSource === album.id && this.state.selectedAlbumId !== album.id ? { color: '#1db954' } : undefined}>{album.name}</AlbumName>
                    <SidebarArtist scroll={isScrolling ? "true" : undefined}>
                      {isScrolling ? displayArtist : displayArtist.substring(0, 25)}
                    </SidebarArtist>
                    {SHOW_ALBUM_STATUS && (
                      <small style={{ fontSize: '10px', color: album.status === 'Released' ? '#7ddc8e' : '#e6d96b' }}>{album.status}</small>
                    )}
                  </AlbumTextWrapper>
                )}
                {!this.state.sidebarCollapsed && this.state.showPinned && (
                  <IconButton
                    aria-label={this.state.pinned[album.id] ? 'Unpin album' : 'Pin album'}
                    title={this.state.pinned[album.id] ? 'Unpin album' : 'Pin to top'}
                    style={{
                      marginLeft: 'auto',
                      padding: '0 8px',
                      fontSize: '16px',
                      color: this.state.pinned[album.id] ? '#1db954' : '#fff',
                      opacity: this.state.pinned[album.id] ? 1 : 0.35,
                    }}
                    onClick={(e) => this.togglePinned(album.id, e)}><PinIcon filled={!!this.state.pinned[album.id]} /></IconButton>
                )}
              </AlbumItemRow>
            );
          })}
          <AlbumItemRow onClick={() => this.openPrompt('album')}>
            <AlbumThumb as="div" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #888' }}>+</AlbumThumb>
            {!this.state.sidebarCollapsed && (
              <AlbumTextWrapper><AlbumName>New album</AlbumName></AlbumTextWrapper>
            )}
          </AlbumItemRow>

          </SidebarScroll>

          <ProfileButton collapsed={this.state.sidebarCollapsed ? "true" : undefined}
            onClick={() => this.props.history.push('/profile')}
            title="Profile" aria-label="Profile"
            style={onOwnProfile ? { color: '#1db954' } : undefined}>
            <span className="profile-avatar"
              style={sidebarUser.pfpURL ? { backgroundImage: `url("${sidebarUser.pfpURL}")` } : undefined}>
              {!sidebarUser.pfpURL && profileLabel.charAt(0).toUpperCase()}
            </span>
            {!this.state.sidebarCollapsed && <span className="profile-name">{profileLabel}</span>}
          </ProfileButton>
        </SidebarContainer>

        {/* Search results (the album view below is only hidden, so edits in progress aren't lost) */}
        {results && (
          <div style={{ marginLeft: sidebarWidth + 'px', paddingBottom: playerHeight }}>
            <div style={{ maxWidth: '1000px', width: '90%', margin: '32px auto' }}>
              <h4>Results for “{this.state.searchQuery.trim()}”</h4>
              {this.state.songIndexLoading && <small className="text-muted">Searching songs…</small>}
              {!this.state.songIndexLoading && !results.artists.length && !results.albums.length && !results.songs.length && (
                <p className="text-muted">No results</p>
              )}

              {results.artists.length > 0 && (
                <React.Fragment>
                  <h5 className="mt-4">Artists</h5>
                  <SongList style={{ margin: '8px 0', width: '100%' }}>
                    {results.artists.map((artist) => (
                      <Song key={artist.name} onClick={() => this.setState({ searchQuery: artist.name })}>
                        <span className="d-flex align-items-center">
                          <AlbumThumb as="div" style={{ borderRadius: '50%', background: 'var(--placeholder-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                            {artist.name.charAt(0).toUpperCase()}
                          </AlbumThumb>
                          <AlbumTextWrapper>
                            <AlbumName>{artist.name}</AlbumName>
                            <small className="text-muted">{artist.albumCount} {artist.albumCount === 1 ? 'album' : 'albums'}</small>
                          </AlbumTextWrapper>
                        </span>
                      </Song>
                    ))}
                  </SongList>
                </React.Fragment>
              )}

              {results.albums.length > 0 && (
                <React.Fragment>
                  <h5 className="mt-4">Albums</h5>
                  <SongList style={{ margin: '8px 0', width: '100%' }}>
                    {results.albums.map((album) => (
                      <Song key={album.id} onClick={() => this.openAlbum(album.id)}>
                        <span className="d-flex align-items-center">
                          <AlbumThumb src={album.coverUrl || coverPlaceholder(album.name)} alt={album.name} />
                          <AlbumTextWrapper>
                            <AlbumName>{album.name}</AlbumName>
                            <small className="text-muted">{this.artistLine(album.id, album.artist)}</small>
                          </AlbumTextWrapper>
                        </span>
                      </Song>
                    ))}
                  </SongList>
                </React.Fragment>
              )}

              {results.songs.length > 0 && (
                <React.Fragment>
                  <h5 className="mt-4">Songs</h5>
                  <SongList style={{ margin: '8px 0', width: '100%' }}>
                    {results.songs.slice(0, MAX_SONG_RESULTS).map((song) => (
                      <Song key={song.id} current={nowPlaying && nowPlaying.id === song.id ? "true" : undefined}
                        onClick={() => this.openAlbum(song.albumId, song.id)}>
                        <span className="d-flex align-items-center">
                          <AlbumThumb src={song.album.coverUrl || coverPlaceholder(song.album.name)} alt={song.album.name} />
                          <AlbumTextWrapper>
                            <AlbumName>{song.name}</AlbumName>
                            <small className="text-muted">{song.album.name} · {this.artistLine(song.album.id, song.album.artist)}</small>
                          </AlbumTextWrapper>
                        </span>
                        {nowPlaying && nowPlaying.id === song.id
                          ? <NowPlayingIcon playing={this.state.isPlaying} />
                          : <span>▶️</span>}
                      </Song>
                    ))}
                  </SongList>
                  {results.songs.length > MAX_SONG_RESULTS && (
                    <small className="text-muted">Showing the first {MAX_SONG_RESULTS} of {results.songs.length} songs. Keep typing to narrow it down.</small>
                  )}
                </React.Fragment>
              )}
            </div>
          </div>
        )}

        {/* Profile (inside the app shell so the player keeps playing) */}
        {showProfile && (
          <div style={{ marginLeft: sidebarWidth + 'px', paddingBottom: playerHeight, display: results ? 'none' : undefined }}>
            {onBandPage
              ? <Band key={pathname} bandId={decodeURIComponent(pathname.slice(3))} />
              : <Profile key={viewedProfile || 'me'} username={viewedProfile} />}
          </div>
        )}

        <div style={{ marginLeft: sidebarWidth + 'px', paddingBottom: playerHeight, display: results || showProfile ? 'none' : undefined }}>
          {this.state.emailStatus && !this.state.emailStatus.hasEmail && !this.state.emailPromptHidden && (
            <Alert variant="warning" dismissible className="m-3"
              onClose={() => { snoozeEmailPrompt(); this.setState({ emailPromptHidden: true }); }}>
              {this.state.emailStatus.pending
                ? "Check your inbox to verify your email address."
                : "Add an email address so you can reset your password if you forget it."}{' '}
              <Link to="/profile">Open profile</Link>
            </Alert>
          )}
          <AlbumLayout split={playlist ? undefined : "true"} style={{ '--player-h': `${playerHeight}px` }}>
          <div className="album-side">
          <div className="album-header">
          {playlist ? (
            <React.Fragment>
              <AlbumThumb as="div" style={{ width: '128px', height: '128px', margin: '24px auto', fontSize: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1db954, #0d6efd)', color: '#fff', borderRadius: '8px' }}>{playlist.icon}</AlbumThumb>
              <AlbumTitle>{playlist.name}</AlbumTitle>
              <AlbumArtist>{this.state.playlistLoading ? 'Loading…' : `${this.state.songs.length} ${this.state.songs.length === 1 ? 'song' : 'songs'}`}</AlbumArtist>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <AlbumCover
                src={this.state.albumUrl
                  ? this.state.albumUrl + this.state.coverBust
                  : coverPlaceholder(this.state.albumName)}
                onClick={() => this.state.editMode && this.coverInputRef.current.click()}
                style={this.state.editMode ? {
                  cursor: 'pointer',
                  outline: '2px solid #0d6efd',
                  opacity: this.state.uploadingCover ? 0.5 : 1,
                } : undefined}
              />
              {this.state.editMode && (
                <small className="text-muted album-hint">
                  {this.state.uploadingCover ? 'Uploading…' : 'Click the cover to change it (square GIFs stay animated)'}
                </small>
              )}
              <input ref={this.coverInputRef} type="file" accept="image/*" hidden
                onChange={(e) => { this.pickCover(e.target.files[0]); e.target.value = ''; }} />
              {this.state.editMode ? (
                <React.Fragment>
                  <EditInput centered style={{ fontSize: '1.5rem', fontWeight: 500 }}
                    key={`name-${this.state.selectedAlbumId}-${this.state.albumName}`}
                    defaultValue={this.state.albumName} placeholder="Album name"
                    onBlur={(e) => this.saveAlbumField('name', e.target)}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                  {ALBUM_LINKS.map(service => (
                    <EditInput centered style={{ fontSize: '0.9rem' }}
                      key={`link-${service.id}-${this.state.selectedAlbumId}-${this.state.albumLinks[service.id] || ''}`}
                      defaultValue={this.state.albumLinks[service.id] || ''}
                      placeholder={`${service.label} link`} aria-label={`${service.label} link`}
                      onBlur={(e) => this.saveAlbumLink(service, e.target)}
                      onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                  ))}
                  {SHOW_ALBUM_STATUS && (
                    <React.Fragment>
                      <EditInput centered list="album-status-options"
                        key={`status-${this.state.selectedAlbumId}-${this.state.albumStatus}`}
                        defaultValue={this.state.albumStatus} placeholder="Status"
                        onBlur={(e) => this.saveAlbumField('status', e.target)}
                        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                      <datalist id="album-status-options">
                        {STATUS_SUGGESTIONS.map(option => <option key={option} value={option} />)}
                      </datalist>
                    </React.Fragment>
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <AlbumTitle>{this.state.albumName}</AlbumTitle>
                  <AlbumArtist>
                    {this.artistsOf(this.state.selectedAlbumId, this.state.artistName).map((artist, n) => (
                      <React.Fragment key={`${artist.key}:${artist.name}`}>
                        {n > 0 && ', '}
                        {artist.band ? (
                          <Link to={`/b/${encodeURIComponent(artist.bandId)}`} className="artist-link"
                            title={`View ${artist.name}`}>{artist.name}</Link>
                        ) : artist.username ? (
                          <Link to={`/u/${encodeURIComponent(artist.username)}`} className="artist-link"
                            title={`View ${artist.name}'s profile`}>{artist.name}</Link>
                        ) : artist.name}
                      </React.Fragment>
                    ))}
                  </AlbumArtist>
                  {SHOW_ALBUM_STATUS && this.state.selectedAlbumId && this.state.albumName && (
                    <div className="album-hint mb-2" style={{ marginTop: 0 }}>
                      <Badge variant={statusVariant(this.state.albumStatus)}>{this.state.albumStatus}</Badge>
                    </div>
                  )}
                </React.Fragment>
              )}
              {this.state.selectedAlbumId && (
                <div className="album-actions">
                  {canEdit && this.state.editMode && (
                    <EditButton onClick={this.openArtists}
                      style={{ margin: 0, display: 'inline-flex', alignItems: 'center', padding: '8px 16px' }}>
                      Edit Artists
                    </EditButton>
                  )}
                  {canEdit && (
                    this.state.editMode ? (
                      <EditButton onClick={this.toggleEditMode} aria-label="Save changes" title="Save"
                        style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'var(--edit-accent)', borderColor: 'var(--edit-accent)', color: '#fff' }}>
                        <CheckIcon /> Save
                      </EditButton>
                    ) : (
                      <ActionButton onClick={this.toggleEditMode} aria-label="Edit album" title="Edit">
                        <EditIcon />
                      </ActionButton>
                    )
                  )}
                  {canContribute && !canEdit && !this.state.editMode && (
                    <ActionButton onClick={() => this.openPrompt('song')} aria-label="Add song" title="Add song">
                      <PlusIcon />
                    </ActionButton>
                  )}
                  {canContribute && !this.state.editMode && (
                    <ActionButton onClick={this.toggleAnalytics}
                      aria-label={this.state.analyticsMode ? 'Hide play data' : 'Show play data'}
                      aria-pressed={this.state.analyticsMode}
                      title={this.state.analyticsMode ? 'Hide play data' : 'Play data'}
                      style={this.state.analyticsMode ? { background: 'var(--edit-accent)', borderColor: 'var(--edit-accent)', color: '#fff' } : undefined}>
                      <AnalyticsIcon />
                    </ActionButton>
                  )}
                  {!this.state.editMode && this.state.albumName && !this.isInLibrary(this.state.selectedAlbumId) && (
                    <EditButton onClick={() => this.saveAlbum(this.state.selectedAlbumId)}
                      aria-label="Save album to my albums" title="Save to my albums"
                      style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 16px', lineHeight: 1, background: 'var(--edit-accent)', borderColor: 'var(--edit-accent)', color: '#fff' }}>
                      <BookmarkIcon /> Save
                    </EditButton>
                  )}
                  {!this.state.editMode && this.state.albumName && this.isInLibrary(this.state.selectedAlbumId) && !canContribute && (
                    <ActionButton onClick={() => this.removeAlbum(this.state.selectedAlbumId)}
                      aria-label="Remove from my albums" title="Saved. Click to remove from my albums">
                      <BookmarkIcon filled />
                    </ActionButton>
                  )}
                  {!this.state.editMode && this.state.albumName && albumLinkList(this.state.albumLinks).map(link => {
                    const LinkIcon = { spotify: SpotifyIcon, youtube: YouTubeIcon, appleMusic: AppleMusicIcon }[link.id];
                    return (
                      <ActionButton as="a" key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                        aria-label={`Listen on ${link.label}`} title={`Listen on ${link.label}`}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'inherit', textDecoration: 'none' }}>
                        <LinkIcon />
                      </ActionButton>
                    );
                  })}
                  {!this.state.editMode && (
                    <ActionButton onClick={this.openShare}
                      aria-label="Share album" title="Share">
                      <ShareIcon />
                    </ActionButton>
                  )}
                </div>
              )}
            </React.Fragment>
          )}
          </div>
          {showExtras && renderExtras('extras-column')}
          </div>
          <div className="album-tracks">
          <SongList>
            {rows.map((row, i) => { const si = row.songIndex; const name = row.name; const isCurrent = !!nowPlaying && nowPlaying.id === row.id && nowPlaying.url === row.url; return (
              <Song key={`${row.id}:${row.versionId || ''}`}
                current={isCurrent ? "true" : undefined}
                aria-current={isCurrent ? "true" : undefined}
                onClick={() => !this.state.editMode && this.playSong(i)}
                onDragOver={(e) => {
                  if (this.dragFrom === undefined) return;
                  e.preventDefault();
                  if (this.state.dragOverIndex !== i) this.setState({ dragOverIndex: i });
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = this.dragFrom;
                  this.dragFrom = undefined;
                  this.setState({ dragOverIndex: null });
                  if (from !== undefined) this.moveSong(from, i);
                }}
                style={{
                  position: "relative",
                  cursor: this.state.editMode ? 'default' : 'pointer',
                  ...(this.state.editMode && { padding: '10px 12px 10px 14px' }),
                  ...(this.state.dragOverIndex === i && { boxShadow: '0 -3px 0 #0d6efd' }),
                }}>
                {this.state.editMode ? (
                  <React.Fragment>
                    <span draggable aria-label="Drag to reorder" title="Drag to reorder"
                      style={{ cursor: 'grab', marginRight: '6px', color: '#888', userSelect: 'none', display: 'inline-flex', fontSize: '20px' }}
                      onDragStart={(e) => {
                        this.dragFrom = i;
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(i));
                        e.dataTransfer.setDragImage(e.currentTarget.closest('li'), 0, 0);
                      }}
                      onDragEnd={() => { this.dragFrom = undefined; this.setState({ dragOverIndex: null }); }}><GripIcon /></span>
                    <EditInput
                      key={`${this.state.songs[si]}-${name}`}
                      defaultValue={name}
                      onBlur={(e) => this.renameSong(si, e.target)}
                      onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '8px', flexShrink: 0 }}>
                      <RowAction aria-label="Move up" title="Move up" disabled={i === 0}
                        onClick={() => this.moveSong(si, si - 1)}><ChevronUpIcon /></RowAction>
                      <RowAction aria-label="Move down" title="Move down" disabled={si === this.state.songs.length - 1}
                        onClick={() => this.moveSong(si, si + 1)}><ChevronDownIcon /></RowAction>
                      <RowAction aria-label="Song artists" title="Song artists" onClick={() => this.openSongArtists(si)}><MicIcon /></RowAction>
                      <span aria-hidden="true" style={{ width: '1px', height: '20px', margin: '0 4px', background: 'var(--edit-border)' }} />
                      <RowAction danger="true" aria-label="Delete song" title="Delete song" onClick={() => this.deleteSong(si)}><TrashIcon /></RowAction>
                    </span>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <span style={playlist ? { display: 'flex', alignItems: 'center', minWidth: 0 } : undefined}>
                      {playlist && this.state.songMeta[si] && (
                        <AlbumThumb
                          loading="lazy"
                          src={this.state.songMeta[si].coverUrl || coverPlaceholder(this.state.songMeta[si].albumTitle)}
                          alt={this.state.songMeta[si].albumTitle}
                          style={{ width: '48px', height: '48px', flexShrink: 0, marginRight: '16px' }} />
                      )}
                      <span style={{ minWidth: 0, ...(isCurrent && { color: 'var(--song-current-text)', fontWeight: 600 }) }}>
                        {isCurrent && <NowPlayingIcon playing={this.state.isPlaying} style={{ marginRight: '8px', verticalAlign: '-1px' }} />}
                        {name}
                        {!playlist && !row.url && (
                          <small className="text-muted ml-2">No versions</small>
                        )}
                        {!playlist && row.artistKeys.length > 0 && (
                          <small className="text-muted" style={{ display: 'block' }}>
                            {this.artistLine(row.albumId, '', row.artistKeys)}
                          </small>
                        )}
                        {playlist && this.state.songMeta[si] && (
                          <small className="text-muted" style={{ display: 'block' }}>
                            {this.state.songMeta[si].albumTitle} · {this.artistLine(this.state.songMeta[si].albumId, this.state.songMeta[si].artist, row.artistKeys)}
                          </small>
                        )}
                      </span>
                    </span>
                    {playlist ? (
                      <IconButton aria-label="Remove from saved songs" title="Remove from saved songs"
                        style={{ color: '#e0245e' }}
                        onClick={(e) => { e.stopPropagation(); this.removeSaved(si); }}>♥</IconButton>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, position: 'relative' }}>
                        {showPlays && (
                          // Play data replaces the controls but they stay laid out (hidden), so the row doesn't change size.
                          <span title="Plays" aria-label={row.versionId ? undefined : `${this.state.songListens[row.id] || 0} plays`}
                            style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {!row.versionId && (
                              <React.Fragment>
                                {this.state.songListens[row.id] === undefined ? '…' : this.state.songListens[row.id]}
                                <small className="text-muted ml-2">plays</small>
                              </React.Fragment>
                            )}
                          </span>
                        )}
                        <span style={{ display: 'flex', alignItems: 'center', visibility: showPlays ? 'hidden' : undefined }} aria-hidden={showPlays ? 'true' : undefined}>
                        {this.state.isBuffering && nowPlaying && nowPlaying.id === row.id && nowPlaying.url === row.url && (
                          <Spinner animation="border" size="sm" role="status" aria-label="Loading song" />
                        )}
                        {this.state.selectedAlbumId && (
                          <IconButton
                            aria-label={this.state.savedIds[row.id] ? 'Remove from saved songs' : 'Save song'}
                            title={this.state.savedIds[row.id] ? 'Remove from saved songs' : 'Save song'}
                            style={{ color: this.state.savedIds[row.id] ? '#e0245e' : '#888' }}
                            onClick={(e) => { e.stopPropagation(); this.toggleSaved(row.id, this.state.selectedAlbumId); }}>
                            {this.state.savedIds[row.id] ? '♥' : '♡'}
                          </IconButton>
                        )}
                        <span data-song-menu style={{ cursor: "pointer", marginLeft: '8px' }} onClick={(e) => this.toggleMenu(i, e)}>⋮</span>
                        </span>
                      </span>
                    )}
                  </React.Fragment>
                )}
                {!this.state.editMode && this.state.menuOpenIndex === i && (
                  <OptionsMenu data-song-menu below={this.state.menuOpenBelow ? 'true' : undefined} onClick={(e) => e.stopPropagation()}>
                    {(!this.state.selectedAlbumId || canContribute) && (
                      <button onClick={() => this.openVersions(si)}>Versions</button>
                    )}
                    {this.state.selectedAlbumId && canEdit && (
                      <button onClick={() => this.openSongArtists(si)}>Artists</button>
                    )}
                    {this.state.selectedAlbumId && canEdit && this.state.userAlbums.length > 1 && (
                      <button onClick={() => this.openMove(si)}>Move to album</button>
                    )}
                    {this.state.selectedAlbumId && canEdit && (
                      <button onClick={() => this.openMerge(si)}>Move into a song as version</button>
                    )}
                    {this.state.selectedAlbumId && (
                      <button onClick={() => this.copyShareLink(`/s/${this.state.selectedAlbumId}/${row.id}`)}>Copy link</button>
                    )}
                    <button onClick={() => this.downloadFile(row.url)}>Download</button>
                  </OptionsMenu>
                )}
              </Song>
            ); })}
          </SongList>
          {playlist && !this.state.playlistLoading && this.state.songs.length === 0 && (
            <p className="text-muted text-center">No saved songs yet</p>
          )}
          {this.state.editMode && (
            <EditButton onClick={() => this.openPrompt('song')}
              style={{ width: '100%', margin: '8px 0', padding: '12px 16px', borderRadius: '6px', borderStyle: 'dashed', borderColor: 'var(--edit-border)', color: 'inherit', opacity: 0.8 }}>
              + Add song
            </EditButton>
          )}
          </div>
          {showExtras && renderExtras('extras-below')}
          </AlbumLayout>

        </div>

        {/* Album cover cropper */}
        {this.state.cropFile && (
          <PfpCropper file={this.state.cropFile} title="Album cover" circle={false} output={1000}
            saving={this.state.uploadingCover} onSave={this.saveCover} onCancel={() => this.setState({ cropFile: null })} />
        )}

        {/* Name prompt (new album / new song) */}
        <Modal show={!!this.state.prompt} onHide={this.closePrompt} centered>
          {this.state.prompt && (
            <Form onSubmit={this.submitPrompt}>
              <Modal.Header closeButton>
                <Modal.Title>{this.state.prompt.kind === 'album' ? 'New album' : 'New song'}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Control
                  autoFocus
                  placeholder={this.state.prompt.kind === 'album' ? 'Album name' : 'Song name'}
                  value={this.state.prompt.value}
                  onChange={(e) => this.setState({ prompt: { ...this.state.prompt, value: e.target.value } })}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.closePrompt}>Cancel</Button>
                <Button type="submit" disabled={!this.state.prompt.value.trim()}>Create</Button>
              </Modal.Footer>
            </Form>
          )}
        </Modal>

        {/* Song versions */}
        <Modal show={!!this.state.versionsDialog} onHide={this.closeVersions} centered scrollable>
          {this.state.versionsDialog && (() => {
            const vd = this.state.versionsDialog;
            const uploading = vd.uploading !== null;
            // Collaborators can add versions; changing or removing them is for owners.
            const manage = !vd.albumId || this.canEditAlbum();
            return (
              <React.Fragment>
                <Modal.Header closeButton>
                  <Modal.Title>Versions · {vd.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form.Control
                    placeholder="What changed in the new version? (optional)"
                    value={vd.description}
                    disabled={uploading}
                    onChange={(e) => this.updateVersions(vd.songId, { description: e.target.value })}
                  />
                  <Button className="mt-2" block disabled={uploading || vd.loading || vd.busy} onClick={this.pickVersionFile}>
                    {uploading ? 'Uploading…' : 'Upload new version'}
                  </Button>
                  {uploading && <UploadProgress className="mt-2" now={vd.uploading} label={`${vd.uploading}%`} />}
                  <hr />
                  {manage && vd.versions.length > 1 && (
                    <p className="small text-muted">
                      Tick "Show in album" to list a version as its own row. When several are shown, each name gets its version number.
                    </p>
                  )}
                  {vd.loading && <div className="text-center"><Spinner animation="border" /></div>}
                  {!vd.loading && vd.versions.length === 0 && <p className="text-muted text-center">No versions yet</p>}
                  {vd.versions.map((v) => {
                    const isDefault = v.id === vd.defaultVersion;
                    return (
                      <div key={v.id} className="d-flex align-items-center py-2 border-bottom">
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                          <strong>Version {v.versionNumber}</strong>
                          {isDefault && <Badge variant="success" className="ml-2">DEFAULT</Badge>}
                          <div className="text-muted small">
                            {v.timestamp ? new Date(v.timestamp * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                          </div>
                          {manage && vd.editingId === v.id ? (
                            <EditInput
                              autoFocus
                              style={{ width: '100%' }}
                              defaultValue={v.description}
                              placeholder="e.g. Fixed bridge timing, new mix…"
                              onBlur={(e) => this.saveVersionDescription(v.id, e.target)}
                              onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                          ) : (
                            <div className="small text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                              {v.description || 'No description'}
                            </div>
                          )}
                          {manage && <Form.Check custom type="checkbox" className="small mt-1"
                            id={`show-version-${v.id}`}
                            label={isDefault ? 'Shown in album (default)' : 'Show in album'}
                            checked={isDefault || v.visible}
                            disabled={isDefault || vd.busy || uploading}
                            onChange={() => this.toggleVersionVisible(v.id)} />}
                        </div>
                        <div className="d-flex align-items-center flex-shrink-0 ml-2">
                          {manage && !isDefault && (
                            <Button size="sm" variant="outline-success" disabled={vd.busy || uploading}
                              onClick={() => this.setDefaultVersion(v.id)}>Set default</Button>
                          )}
                          {manage && <IconButton aria-label="Edit description" title="Edit description"
                            onClick={() => this.updateVersions(vd.songId, { editingId: v.id })}>✏️</IconButton>}
                          <IconButton as="a" aria-label="Download version" title="Download" href={v.url}
                            target="_blank" rel="noopener noreferrer">⬇️</IconButton>
                          {manage && !isDefault && vd.versions.length > 1 && (
                            <IconButton aria-label="Delete version" title="Delete version" disabled={vd.busy || uploading}
                              onClick={() => this.deleteVersion(v.id)}>🗑</IconButton>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Modal.Body>
              </React.Fragment>
            );
          })()}
        </Modal>

        {/* Share album: link + who is on it */}
        <Modal show={!!this.state.shareDialog} onHide={this.closeShare} centered scrollable>
          {this.state.shareDialog && (() => {
            const sd = this.state.shareDialog;
            const owners = this.state.albumOwners;
            const collaborators = this.state.albumCollaborators.filter(c => !owners.includes(c));
            const everyone = [...owners, ...collaborators];
            const isOwner = this.isAlbumOwner();
            const resolved = this.state.albumArtists[sd.albumId] || [];
            const nameOf = (username) => sd.names[username] || (resolved.find(a => a.username === username) || {}).name || username;
            const q = normalizeText(sd.query.trim());
            const matches = q && sd.directory
              ? sd.directory.filter(u => !everyone.includes(u.username)
                  && (normalizeText(u.name).includes(q) || normalizeText(u.username).includes(q))).slice(0, 8)
              : [];
            const person = (username, buttons) => (
              <div key={username} className="d-flex align-items-center justify-content-between py-1">
                <div style={{ minWidth: 0 }}>
                  <Link to={`/u/${encodeURIComponent(username)}`} onClick={this.closeShare}>{nameOf(username)}</Link>
                  <small className="text-muted ml-2">@{username}</small>
                  {username === this.state.userId && <small className="text-muted ml-2">You</small>}
                </div>
                {(isOwner || username === this.state.userId) && <div className="d-flex flex-shrink-0 ml-2">{buttons}</div>}
              </div>
            );
            const onlyOwner = owners.length < 2;
            return (
              <React.Fragment>
                <Modal.Header closeButton>
                  <Modal.Title>Share "{this.state.albumName}"</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <h6>Listen link</h6>
                  <div className="d-flex mb-1">
                    <Form.Control readOnly size="sm" value={siteUrl(`/a/${sd.albumId}`)}
                      aria-label="Share link" onFocus={(e) => e.target.select()} />
                    <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0"
                      onClick={() => this.copyShareLink(`/a/${sd.albumId}`)}>Copy</Button>
                  </div>
                  <small className="text-muted">Listeners: anyone with the link can listen, no account needed.</small>

                  <h6 className="mt-4">Owners</h6>
                  {owners.length === 0 && <div className="text-muted">Nobody yet.</div>}
                  {owners.map(username => person(username, (
                    <React.Fragment>
                      <Button size="sm" variant="outline-secondary" disabled={sd.busyUser === username || onlyOwner}
                        title={onlyOwner ? 'An album needs at least one owner' : undefined}
                        onClick={() => (username !== this.state.userId
                          || window.confirm('Step down as an owner? You will stay on the album as a collaborator.'))
                          && this.changeRole(username, 'collaborator')}>
                        {username === this.state.userId ? 'Step down' : 'Make collaborator'}
                      </Button>
                      {username === this.state.userId ? (
                        <Button size="sm" variant="outline-danger" className="ml-2" disabled={sd.busyUser === username || onlyOwner}
                          title={onlyOwner ? 'An album needs at least one owner' : 'Leave, and keep the album saved'}
                          onClick={this.leaveAlbum}>Leave</Button>
                      ) : (
                        <Button size="sm" variant="outline-danger" className="ml-2" disabled={sd.busyUser === username || onlyOwner}
                          onClick={() => window.confirm(`Remove ${nameOf(username)} from this album?`) && this.changeRole(username, 'none')}>Remove</Button>
                      )}
                    </React.Fragment>
                  )))}
                  <small className="text-muted d-block mt-1">
                    Owners can edit everything, including the cover, details, songs, artists and who is on the album.
                  </small>

                  <h6 className="mt-4">Collaborators</h6>
                  {collaborators.length === 0 && <div className="text-muted">Nobody yet.</div>}
                  {collaborators.map(username => person(username, (
                    <React.Fragment>
                      {isOwner && (
                        <Button size="sm" variant="outline-secondary" disabled={sd.busyUser === username}
                          onClick={() => this.changeRole(username, 'owner')}>Make owner</Button>
                      )}
                      {username === this.state.userId ? (
                        <Button size="sm" variant="outline-danger" className={isOwner ? 'ml-2' : undefined} disabled={sd.busyUser === username}
                          title="Leave, and keep the album saved" onClick={this.leaveAlbum}>Leave</Button>
                      ) : (
                        <Button size="sm" variant="outline-danger" className="ml-2" disabled={sd.busyUser === username}
                          onClick={() => this.changeRole(username, 'none')}>Remove</Button>
                      )}
                    </React.Fragment>
                  )))}
                  <small className="text-muted d-block mt-1">
                    Collaborators are credited as artists and can add songs and versions.
                  </small>

                  {isOwner && (
                    <React.Fragment>
                      <h6 className="mt-4">Add someone</h6>
                      <Form.Control size="sm" placeholder={sd.directory ? 'Search by name or username' : 'Loading people…'}
                        disabled={!sd.directory} value={sd.query} aria-label="Search people"
                        onChange={(e) => this.updateShare({ query: e.target.value })} />
                      {matches.map(user => (
                        <div key={user.username} className="d-flex align-items-center justify-content-between py-1">
                          <div style={{ minWidth: 0 }}>
                            {user.name || user.username}<small className="text-muted ml-2">@{user.username}</small>
                          </div>
                          <div className="d-flex flex-shrink-0 ml-2">
                            <Button size="sm" variant="outline-success" disabled={!!sd.busyUser}
                              onClick={() => this.addPerson(user, 'collaborator')}>Collaborator</Button>
                            <Button size="sm" variant="outline-success" className="ml-2" disabled={!!sd.busyUser}
                              onClick={() => this.addPerson(user, 'owner')}>Owner</Button>
                          </div>
                        </div>
                      ))}
                      {q && sd.directory && matches.length === 0 && <div className="text-muted mt-2">No one found.</div>}
                    </React.Fragment>
                  )}
                </Modal.Body>
              </React.Fragment>
            );
          })()}
        </Modal>

        {/* Album artists: who shows on the album, and artists who aren't on Stew */}
        <Modal show={!!this.state.artistsDialog} onHide={this.closeArtists} centered scrollable>
          {this.state.artistsDialog && (() => {
            const d = this.state.artistsDialog;
            const artists = this.state.albumArtists[d.albumId] || [];
            return (
              <React.Fragment>
                <Modal.Header closeButton><Modal.Title>Album artists</Modal.Title></Modal.Header>
                <Modal.Body>
                  <p className="text-muted small">Tick the artists to show under the album name. Everyone stays available to credit on songs.</p>
                  {artists.map(a => (
                    <div key={a.key} className="d-flex align-items-center py-2 border-bottom">
                      <Form.Check custom type="checkbox" id={`album-artist-${a.key}`} className="flex-grow-1"
                        label={<span>{a.name}{' '}<small className="text-muted">{a.external ? 'Not on Stew' : a.band ? 'Band' : a.role === 'owner' ? 'Owner' : a.role === 'collaborator' ? 'Collaborator' : ''}</small></span>}
                        checked={!a.hidden} disabled={d.busy} onChange={() => this.toggleArtistShown(a)} />
                      {(a.external || a.band) && (
                        <Button variant="outline-danger" size="sm" disabled={d.busy} onClick={() => this.removeExternalArtist(a)}>Remove</Button>
                      )}
                    </div>
                  ))}
                  <div className="mt-4">
                    <h6>Add one of your bands</h6>
                    {(() => {
                      if (d.bands === null) return <small className="text-muted">Loading bands…</small>;
                      const available = d.bands.filter(b => !artists.some(a => a.key === b.id));
                      if (available.length === 0) {
                        return <small className="text-muted">{d.bands.length ? 'All your bands are already here.' : 'You are not in any bands yet. Create one from your Profile.'}</small>;
                      }
                      return available.map(b => (
                        <div key={b.id} className="d-flex align-items-center justify-content-between py-1">
                          <div style={{ minWidth: 0 }}>{b.name}</div>
                          <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0" disabled={d.busy}
                            onClick={() => this.addAlbumBand(b)}>Add</Button>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="mt-4">
                    <h6>Add an artist who is on Stew</h6>
                    <Form.Control placeholder={d.directory ? 'Search by name or username' : 'Loading people…'}
                      disabled={!d.directory || d.busy} value={d.query} aria-label="Search people"
                      onChange={(e) => this.setState({ artistsDialog: { ...d, query: e.target.value } })} />
                    {(() => {
                      const q = normalizeText(d.query.trim());
                      const taken = artists.map(a => a.username).filter(Boolean);
                      const found = q && d.directory
                        ? d.directory.filter(u => !taken.includes(u.username)
                            && (normalizeText(u.name).includes(q) || normalizeText(u.username).includes(q))).slice(0, 6)
                        : [];
                      return (
                        <React.Fragment>
                          {found.map(user => (
                            <div key={user.username} className="d-flex align-items-center justify-content-between py-1">
                              <div style={{ minWidth: 0 }}>{user.name || user.username}<small className="text-muted ml-2">@{user.username}</small></div>
                              <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0" disabled={d.busy}
                                onClick={() => this.addAlbumStewArtist(user)}>Add</Button>
                            </div>
                          ))}
                          {q && d.directory && found.length === 0 && <div className="text-muted small mt-2">No one found.</div>}
                        </React.Fragment>
                      );
                    })()}
                    <small className="text-muted d-block mt-1">They're added as a collaborator, not an owner. Make someone an owner from Share.</small>
                  </div>
                  <Form onSubmit={this.submitArtist} className="mt-4">
                    <h6>Add an artist who isn't on Stew</h6>
                    <div className="d-flex">
                      <Form.Control placeholder="Artist name" value={d.name}
                        onChange={(e) => this.setState({ artistsDialog: { ...d, name: e.target.value } })} />
                      <Button type="submit" className="ml-2" disabled={d.busy || !d.name.trim()}>Add</Button>
                    </div>
                  </Form>
                </Modal.Body>
              </React.Fragment>
            );
          })()}
        </Modal>

        {/* Song artists: who is credited on one song */}
        <Modal show={!!this.state.songArtistsDialog} onHide={this.closeSongArtists} centered scrollable>
          {this.state.songArtistsDialog && (() => {
            const d = this.state.songArtistsDialog;
            const artists = this.state.albumArtists[d.albumId] || [];
            return (
              <React.Fragment>
                <Modal.Header closeButton><Modal.Title>Artists on "{d.name}"</Modal.Title></Modal.Header>
                <Modal.Body>
                  <p className="text-muted small">Tick who is credited on this song. With none ticked, it shows the album's artists.</p>
                  {artists.map(a => (
                    <div key={a.key} className="py-2 border-bottom">
                      <Form.Check custom type="checkbox" id={`song-artist-${a.key}`}
                        label={<span>{a.name}{a.external && <small className="text-muted ml-2">Not on Stew</small>}{a.band && <small className="text-muted ml-2">Band</small>}</span>}
                        checked={d.selected.includes(a.key)} disabled={d.busy} onChange={() => this.toggleSongArtist(a.key)} />
                    </div>
                  ))}
                  <div className="mt-4">
                    <h6>Add one of your bands</h6>
                    {(() => {
                      if (d.bands === null) return <small className="text-muted">Loading bands…</small>;
                      const available = d.bands.filter(b => !artists.some(a => a.key === b.id));
                      if (available.length === 0) {
                        return <small className="text-muted">{d.bands.length ? 'All your bands are already here.' : 'You are not in any bands yet. Create one from your Profile.'}</small>;
                      }
                      return available.map(b => (
                        <div key={b.id} className="d-flex align-items-center justify-content-between py-1">
                          <div style={{ minWidth: 0 }}>{b.name}</div>
                          <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0" disabled={d.busy}
                            onClick={() => this.addSongBand(b)}>Add</Button>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="mt-4">
                    <h6>Add an artist who is on Stew</h6>
                    <Form.Control placeholder={d.directory ? 'Search by name or username' : 'Loading people…'}
                      disabled={!d.directory || d.busy} value={d.query} aria-label="Search people"
                      onChange={(e) => this.updateSongArtistsDialog({ query: e.target.value })} />
                    {(() => {
                      const q = normalizeText(d.query.trim());
                      const taken = artists.map(a => a.username).filter(Boolean);
                      const found = q && d.directory
                        ? d.directory.filter(u => !taken.includes(u.username)
                            && (normalizeText(u.name).includes(q) || normalizeText(u.username).includes(q))).slice(0, 6)
                        : [];
                      return (
                        <React.Fragment>
                          {found.map(user => (
                            <div key={user.username} className="d-flex align-items-center justify-content-between py-1">
                              <div style={{ minWidth: 0 }}>{user.name || user.username}<small className="text-muted ml-2">@{user.username}</small></div>
                              <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0" disabled={d.busy}
                                onClick={() => this.addSongStewArtist(user)}>Add</Button>
                            </div>
                          ))}
                          {q && d.directory && found.length === 0 && <div className="text-muted small mt-2">No one found.</div>}
                        </React.Fragment>
                      );
                    })()}
                    <small className="text-muted d-block mt-1">They're added to the album as a collaborator, not an owner.</small>
                  </div>
                  <Form onSubmit={this.addSongArtist} className="mt-4">
                    <h6>Add an artist who isn't on Stew</h6>
                    <div className="d-flex">
                      <Form.Control placeholder="Artist name" value={d.newName}
                        onChange={(e) => this.updateSongArtistsDialog({ newName: e.target.value })} />
                      <Button type="submit" variant="secondary" className="ml-2" disabled={d.busy || !d.newName.trim()}>Add</Button>
                    </div>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={this.closeSongArtists} disabled={d.busy}>Cancel</Button>
                  <Button onClick={this.saveSongArtists} disabled={d.busy}>Save</Button>
                </Modal.Footer>
              </React.Fragment>
            );
          })()}
        </Modal>

        {/* Move song to another album */}
        <Modal show={!!this.state.moveDialog} onHide={this.closeMove} centered scrollable>
          {this.state.moveDialog && (
            <React.Fragment>
              <Modal.Header closeButton>
                <Modal.Title>Move "{this.state.moveDialog.name}"</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {sidebarAlbums
                  .filter(a => a.id !== this.state.selectedAlbumId)
                  .map(a => (
                    <div key={a.id} className="d-flex align-items-center py-2 border-bottom">
                      <AlbumThumb loading="lazy"
                        src={a.coverUrl || coverPlaceholder(a.name)}
                        alt={a.name}
                        style={{ width: '40px', height: '40px', flexShrink: 0, marginRight: '16px' }} />
                      <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <strong>{a.name}</strong>
                        <div className="text-muted small">{this.artistLine(a.id, a.artist)}</div>
                      </div>
                      <Button size="sm" className="ml-2" disabled={this.state.moveDialog.busy}
                        onClick={() => this.moveSongToAlbum(a)}>Move</Button>
                    </div>
                  ))}
              </Modal.Body>
            </React.Fragment>
          )}
        </Modal>

        {/* Move song into another song as a version */}
        <Modal show={!!this.state.mergeDialog} onHide={this.closeMerge} centered scrollable>
          {this.state.mergeDialog && (() => {
            const md = this.state.mergeDialog;
            const tokens = normalizeText(md.query).split(/\s+/).filter(Boolean);
            const albumRank = {};
            sidebarAlbums.forEach((a, n) => { albumRank[a.id] = a.id === md.albumId ? -1 : n; });
            const candidates = this.state.songIndex
              .filter(c => c.id !== md.songId && albumsById[c.albumId]
                && tokens.every(t => normalizeText(`${c.name} ${albumsById[c.albumId].name}`).includes(t)))
              .sort((a, b) => albumRank[a.albumId] - albumRank[b.albumId] || a.name.localeCompare(b.name));
            return (
              <React.Fragment>
                <Modal.Header closeButton>
                  <Modal.Title>Move "{md.name}" into a song</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p className="small text-muted">
                    Its versions are added to the song you pick, and "{md.name}" is removed from this album.
                  </p>
                  <Form.Control autoFocus placeholder="Search songs or albums" value={md.query} disabled={md.busy}
                    onChange={(e) => this.setState({ mergeDialog: { ...md, query: e.target.value } })} />
                  {this.state.songIndexLoading && <div className="text-center mt-3"><Spinner animation="border" size="sm" /></div>}
                  {!this.state.songIndexLoading && candidates.length === 0 && (
                    <p className="text-muted text-center mt-3">No matching songs</p>
                  )}
                  {candidates.slice(0, 50).map(c => (
                    <div key={c.id} className="d-flex align-items-center py-2 border-bottom">
                      <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <strong>{c.name}</strong>
                        <div className="text-muted small">{albumsById[c.albumId].name}</div>
                      </div>
                      <Button size="sm" className="ml-2" disabled={md.busy} onClick={() => this.mergeSongInto(c)}>Move here</Button>
                    </div>
                  ))}
                  {candidates.length > 50 && <small className="text-muted">Showing the first 50. Keep typing to narrow it down.</small>}
                </Modal.Body>
              </React.Fragment>
            );
          })()}
        </Modal>

        {/* Album list sort and filter */}
        <Modal show={this.state.filterOpen} onHide={() => this.setState({ filterOpen: false })} centered size="sm">
          <Modal.Header closeButton>
            <Modal.Title>Albums</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h6>Sort by</h6>
            <Form.Check custom type="radio" id="album-sort-recent" name="album-sort" label="Recently played"
              checked={this.state.albumSort === 'recent'} onChange={() => this.setAlbumPrefs({ albumSort: 'recent' })} />
            <Form.Check custom type="radio" id="album-sort-alpha" name="album-sort" label="Alphabetical"
              checked={this.state.albumSort === 'alpha'} onChange={() => this.setAlbumPrefs({ albumSort: 'alpha' })} />
            <hr />
            <Form.Check custom type="switch" id="album-show-pinned" label="Show pinned"
              checked={this.state.showPinned} onChange={(e) => this.setAlbumPrefs({ showPinned: e.target.checked })} />
            <small className="text-muted">Pinned albums stay at the top, with a pin button on each album. Turn this off to hide the pin buttons and ignore pins when sorting.</small>
          </Modal.Body>
        </Modal>

        {/* Audio Element */}
        <audio
          ref={this.audioRef}
          onEnded={this.playNext}
          onTimeUpdate={this.handleTimeUpdate}
          onLoadStart={() => this.setState({ isBuffering: !!this.loadedSrc })}
          onWaiting={() => this.setState({ isBuffering: true })}
          onCanPlay={() => this.setState({ isBuffering: false })}
          onPlaying={() => { this.setState({ isBuffering: false }); this.pushPresence(); }}
          onPause={this.pushPresence}
          onSeeked={this.pushPresence}
          onLoadedMetadata={this.pushPresence}
          onError={this.handleAudioError}
        />

        {/* Player */}
        <PlayerBar style={{ display: nowPlaying ? undefined : 'none', height: playerHeight, marginLeft: sidebarWidth + 'px', flexDirection: 'row', gap: '16px' }}>
          {nowPlaying && (
            <button type="button" onClick={this.openPlayingAlbum} disabled={!nowPlaying.albumId}
              aria-label={`Open album${playingTitle ? `: ${playingTitle}` : ''}`} title="Open album"
              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, flexShrink: 0, cursor: 'pointer', color: 'inherit' }}>
              {playerCover ? (
                <img src={playerCover} alt=""
                  style={{ width: coverSize, height: coverSize, borderRadius: '4px', objectFit: 'cover' }} />
              ) : (
                <div aria-hidden="true" style={{ width: coverSize, height: coverSize, borderRadius: '4px', background: '#444', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>♪</div>
              )}
            </button>
          )}
          <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: '4px' }}>
          <PlayerHeader onClick={this.togglePlayer} style={{ width: '100%' }}>
            <span>
              {nowPlaying ? nowPlaying.name : "Nothing playing 🎶"}
              {nowPlaying && playingTitle && (
                <React.Fragment>
                  <span aria-hidden="true" style={{ fontWeight: 'normal', color: '#aaa', margin: '0 8px' }}>·</span>
                  <PlayerLink type="button" title="Open album" style={{ fontWeight: 'normal' }}
                    onClick={(e) => { e.stopPropagation(); this.openPlayingAlbum(); }}>{playingTitle}</PlayerLink>
                </React.Fragment>
              )}
              {this.state.isBuffering && nowPlaying && (
                <Spinner animation="border" size="sm" className="ml-2" role="status" aria-label="Loading song" />
              )}
            </span>
            <span aria-label={this.state.playerCollapsed ? 'Expand player' : 'Collapse player'}
              title={this.state.playerCollapsed ? 'Expand player' : 'Collapse player'}
              style={{ fontSize: '18px', lineHeight: 1 }}>
              <ChevronsIcon dir={this.state.playerCollapsed ? 'right' : 'left'} rotate={-90} />
            </span>
          </PlayerHeader>

          {nowPlaying && playingArtist && (
            <PlayerSubtitle>
              {playingArtists.map((artist, n) => (
                <React.Fragment key={`${artist.key}:${artist.name}`}>
                  {n > 0 && <span aria-hidden="true">,</span>}
                  {artist.external ? <span>{artist.name}</span> : (
                    <PlayerLink type="button"
                      onClick={() => (artist.band ? this.props.history.push(`/b/${encodeURIComponent(artist.bandId)}`) : this.openPlayingArtist(artist.username))}
                      title={artist.band ? `View ${artist.name}` : `View ${artist.name}'s profile`}>{artist.name}</PlayerLink>
                  )}
                </React.Fragment>
              ))}
            </PlayerSubtitle>
          )}

          {!this.state.playerCollapsed && (
            <PlayerControlsContainer style={{ width: '100%' }}>
              <ProgressWrapper>
                <span>{this.formatTime(this.state.progress)}</span>
                <ProgressBar
                  type="range"
                  min="0"
                  max={this.state.duration}
                  aria-label="Seek"
                  style={{ '--fill': `${this.state.duration ? (this.state.progress / this.state.duration) * 100 : 0}%` }}
                  value={this.state.progress}
                  onChange={this.handleSeek}
                />
                <span>{this.formatTime(this.state.duration)}</span>
              </ProgressWrapper>

              <ControlsWrapper>
                <PlayButton onClick={this.playPrev} aria-label="Previous song" title="Previous"><PrevIcon /></PlayButton>
                <PlayButton onClick={this.togglePlayback}
                  aria-label={this.state.isPlaying ? 'Pause' : 'Play'} title={this.state.isPlaying ? 'Pause' : 'Play'}>
                  {this.state.isPlaying ? <PauseIcon /> : <PlayIcon />}
                </PlayButton>
                <PlayButton onClick={this.playNext} aria-label="Next song" title="Next"><NextIcon /></PlayButton>
                <LoopButton active={this.state.isLooping ? "true" : undefined} onClick={this.toggleLoop}
                  aria-label={this.state.isLooping ? 'Looping this song' : 'Loop'} title={this.state.isLooping ? 'Looping this song' : 'Loop'}>
                  <LoopIcon one={this.state.isLooping} />
                </LoopButton>
                <VolumeSlider
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  aria-label="Volume"
                  style={{ '--fill': `${this.state.volume * 100}%` }}
                  value={this.state.volume}
                  onChange={(e) => {
                    const vol = parseFloat(e.target.value);
                    this.audioRef.current.volume = vol;
                    this.setState({ volume: vol });
                  }}
                />
              </ControlsWrapper>
            </PlayerControlsContainer>
          )}
          </div>
        </PlayerBar>
      </React.Fragment>
    );
  }
}

export default App;
